import React, { useRef, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useProjectStore } from '../../store/projectStore';
import { useCanvasCollabStore } from '../../store/canvasCollabStore';
import { PlacedEquipment, CanvasBox, CanvasLabel } from '../../types';
import RackComponent from './RackComponent';
import EquipmentComponent from './EquipmentComponent';
import FloorplanImage from './FloorplanImage';
import APRangeVisualization from './APRangeVisualization';
import CablesLayer from './CablesLayer';
import FloorPlanLayer from './FloorPlan/FloorPlanLayer';
import FloorPlanToolbar from './FloorPlan/FloorPlanToolbar';
import WallDraftPreview from './FloorPlan/WallDraftPreview';
import { useFloorPlanInteraction } from './FloorPlan/useFloorPlanInteraction';
import { useReferenceImageTransform } from './FloorPlan/useReferenceImageTransform';
import DesignBrowser from './DesignBrowser';
import LogicStatusDrawer from './Logic/LogicStatusDrawer';
import { LogicSimulationHost } from './Logic/LogicSimulationHost';
import { DEFAULT_PIXELS_PER_METER, getFloorPlanReferenceImages } from '../../utils/floorPlanGeometry';
import { isVisibleOnCanvas } from '../../utils/browserVisibility';
import { canvasIconSizePx } from '../../utils/canvasIcon';
import { uniqueCablesById } from '../../utils/cableRuns';
import { applyElementCanvasPos, clientToCanvas, getPanningLayer } from '../../utils/canvasDrag';
import { getEquipmentCablePortCount, NAME_BADGE_FALLBACK_WIDTH, NAMEPLATE_ROW_PX } from '../../utils/cableGeometry';
import { CANVAS_ZOOM_LEVELS } from '../../utils/canvasCameraPrefs';
import { Server, Package, Cable, Circle, Trash2, X, SquareDashed, Type, Move, Maximize2, Cpu, CheckCircle2, AlertTriangle } from 'lucide-react';
import { formatCurrencyNoDecimals } from '../../utils/currencyUtils';
import { useLogicStore, logicHealthOk } from '../../store/logicStore';
import NodeContextMenu from './NodeContextMenu';
import { FloatingEquipmentPanelHost } from './FloatingEquipmentPanel';
import CableRunRoutingPopover from './CableRunRoutingPopover';
// import CableTypeModal from './CableTypeModal'; // No longer needed

// Utility: convert hex colour to rgba with desired alpha for subtle patterns
const toRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const canvasGridStyle = (gridSize: number, zoom: number, dark: boolean): React.CSSProperties => {
  const minor = Math.max(1, gridSize) * zoom;
  const major = minor * 5;
  // 1px screen lines â€” subpixel strokes drop out; keep weight fixed so zoom doesn't fatten them
  const minorLine = 1;
  const majorLine = 2;
  const minorColor = dark ? 'rgba(255,255,255,0.032)' : 'rgba(15,23,42,0.045)';
  const majorColor = dark ? 'rgba(255,255,255,0.055)' : 'rgba(15,23,42,0.065)';
  return {
    backgroundImage: [
      `linear-gradient(to right, ${majorColor} ${majorLine}px, transparent ${majorLine}px)`,
      `linear-gradient(to bottom, ${majorColor} ${majorLine}px, transparent ${majorLine}px)`,
      `linear-gradient(to right, ${minorColor} ${minorLine}px, transparent ${minorLine}px)`,
      `linear-gradient(to bottom, ${minorColor} ${minorLine}px, transparent ${minorLine}px)`,
    ].join(', '),
    backgroundSize: `${major}px ${major}px, ${major}px ${major}px, ${minor}px ${minor}px, ${minor}px ${minor}px`,
  };
};

const Canvas: React.FC = () => {
  // Simple local ID generator for canvas boxes
  const boxIdRef = useRef(0);

  const collabGhosts = useCanvasCollabStore((s) => s.ghosts);

  const canvasRef = useRef<HTMLDivElement>(null);
  const panningLayerRef = useRef<HTMLDivElement>(null);
  const gridOverlayRef = useRef<HTMLDivElement>(null);
  // Removed CableTypeModal state - now using direct createCableRun
  
  // Get panning and zoom state from store
  const { canvasPanX, canvasPanY, canvasZoom, setCanvasPan, setCanvasZoom } = useProjectStore(
    useShallow((s) => ({
      canvasPanX: s.canvasPanX,
      canvasPanY: s.canvasPanY,
      canvasZoom: s.canvasZoom,
      setCanvasPan: s.setCanvasPan,
      setCanvasZoom: s.setCanvasZoom,
    }))
  );

  const panRef = useRef({ x: canvasPanX, y: canvasPanY });
  const lastPanPointRef = useRef<{ x: number; y: number } | null>(null);
  const isPanningRef = useRef(false);
  const panPendingRef = useRef(false);
  const panDocListenersRef = useRef<(() => void) | null>(null);
  const didPanGestureRef = useRef(false);
  const PAN_CLICK_THRESHOLD_PX = 4;

  const applyGridPan = (x: number, y: number) => {
    if (gridOverlayRef.current) {
      gridOverlayRef.current.style.backgroundPosition = `${x}px ${y}px`;
    }
  };

  const setPanningCursor = (on: boolean) => {
    if (canvasRef.current) {
      canvasRef.current.style.cursor = on ? 'grabbing' : '';
    }
    if (panningLayerRef.current) {
      panningLayerRef.current.style.willChange = on ? 'left, top' : 'auto';
    }
  };

  const setPanTextSelectLock = (on: boolean) => {
    // Prevent drag-selecting text on the canvas, floating panels, etc. while panning
    try {
      if (on) {
        window.getSelection()?.removeAllRanges();
        document.body.style.userSelect = 'none';
        (document.body.style as any).webkitUserSelect = 'none';
      } else {
        document.body.style.userSelect = '';
        (document.body.style as any).webkitUserSelect = '';
        document.documentElement.classList.remove('canvas-is-panning');
      }
    } catch { /* ignore */ }
  };

  const setPanHoverLock = (on: boolean) => {
    try {
      document.documentElement.classList.toggle('canvas-is-panning', on);
    } catch { /* ignore */ }
  };

  useLayoutEffect(() => {
    panRef.current = { x: canvasPanX, y: canvasPanY };
    if (panningLayerRef.current && !isPanningRef.current) {
      panningLayerRef.current.style.left = `${canvasPanX}px`;
      panningLayerRef.current.style.top = `${canvasPanY}px`;
    }
    if (!isPanningRef.current) applyGridPan(canvasPanX, canvasPanY);
  }, [canvasPanX, canvasPanY]);

  useEffect(() => {
    const persistLiveView = () => {
      setCanvasPan(panRef.current.x, panRef.current.y);
    };
    window.addEventListener('pagehide', persistLiveView);
    return () => window.removeEventListener('pagehide', persistLiveView);
  }, [setCanvasPan]);

  const applyPanDelta = (clientX: number, clientY: number) => {
    const last = lastPanPointRef.current;
    if (!last) return;
    const next = {
      x: panRef.current.x + (clientX - last.x),
      y: panRef.current.y + (clientY - last.y),
    };
    panRef.current = next;
    lastPanPointRef.current = { x: clientX, y: clientY };
    if (panningLayerRef.current) {
      panningLayerRef.current.style.left = `${next.x}px`;
      panningLayerRef.current.style.top = `${next.y}px`;
    }
    applyGridPan(next.x, next.y);
  };

  const detachPanDocListeners = () => {
    panDocListenersRef.current?.();
    panDocListenersRef.current = null;
  };

  const finishPan = () => {
    const wasPanning = isPanningRef.current;
    panPendingRef.current = false;
    isPanningRef.current = false;
    lastPanPointRef.current = null;
    detachPanDocListeners();
    setPanTextSelectLock(false);
    if (!wasPanning) {
      setPanningCursor(false);
      return;
    }
    didPanGestureRef.current = true;
    setCanvasPan(panRef.current.x, panRef.current.y);
    setPanningCursor(false);
  };

  const consumePanGestureClick = () => {
    if (!didPanGestureRef.current) return false;
    didPanGestureRef.current = false;
    return true;
  };

  const attachPanDocListeners = () => {
    if (panDocListenersRef.current) return;
    const onMove = (e: MouseEvent) => {
      if (panPendingRef.current && !isPanningRef.current && lastPanPointRef.current) {
        const dx = e.clientX - lastPanPointRef.current.x;
        const dy = e.clientY - lastPanPointRef.current.y;
        if (Math.hypot(dx, dy) < PAN_CLICK_THRESHOLD_PX) return;
        panPendingRef.current = false;
        isPanningRef.current = true;
        didPanGestureRef.current = true;
        setPanningCursor(true);
        setPanHoverLock(true);
      }
      if (isPanningRef.current) {
        e.preventDefault();
        applyPanDelta(e.clientX, e.clientY);
      }
    };
    const onUp = () => {
      finishPan();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onUp);
    panDocListenersRef.current = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
    };
  };

  useEffect(() => {
    document.documentElement.classList.remove('canvas-is-panning');
    return () => {
      detachPanDocListeners();
      setPanTextSelectLock(false);
    };
  }, []);

  // Helper function to convert mouse coordinates to canvas coordinates accounting for zoom
  const getCanvasCoordinates = useCallback((clientX: number, clientY: number) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const pan = panRef.current;
    return {
      x: (clientX - rect.left - pan.x) / canvasZoom,
      y: (clientY - rect.top - pan.y) / canvasZoom
    };
  }, [canvasZoom]);
  
  // Local state for canvas toolbar lists (collapsed by default)
  const [nodeContextMenu, setNodeContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({ isOpen: false, position: { x: 0, y: 0 } });
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'equipment' | 'cable';
    id: string;
    label: string;
  } | null>(null);
  // Simple visual "boxes" drawn on the canvas (now persisted with design)
  const [boxes, setBoxes] = useState<CanvasBox[]>([]);
  const [isDrawingBox, setIsDrawingBox] = useState(false);
  const [cableRoutingPopoverOpen, setCableRoutingPopoverOpen] = useState(false);
  const [selectedBoxStyle, setSelectedBoxStyle] = useState<{ stroke: string; fill?: string } | null>(null);
  const [lastSelectedBoxColor, setLastSelectedBoxColor] = useState<string>('#60a5fa'); // Default to pastel blue
  const [boxStart, setBoxStart] = useState<{ x: number; y: number } | null>(null);
  const [boxPreview, setBoxPreview] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    stroke: string;
    fill?: string;
  } | null>(null);
  const [showBoxPalette, setShowBoxPalette] = useState(false);
  const boxPaletteRef = useRef<HTMLDivElement>(null);
  const boxPaletteButtonRef = useRef<HTMLButtonElement>(null);
  
  // Close box palette when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showBoxPalette) {
        const target = event.target as Node;
        // Don't close if clicking on the palette or the button
        if (
          boxPaletteRef.current && 
          !boxPaletteRef.current.contains(target) &&
          boxPaletteButtonRef.current &&
          !boxPaletteButtonRef.current.contains(target)
        ) {
          setShowBoxPalette(false);
        }
      }
    };
    
    if (showBoxPalette) {
      // Use a small delay to allow button's onClick to fire first
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showBoxPalette]);

  const [showBoxTools, setShowBoxTools] = useState(false);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [draggingBoxId, setDraggingBoxId] = useState<string | null>(null);
  const [boxDragOffset, setBoxDragOffset] = useState<{ dx: number; dy: number } | null>(null);
  const [resizingBoxId, setResizingBoxId] = useState<string | null>(null);
  const [resizeHandle, setResizeHandle] = useState<'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw' | null>(null);
  const [resizeStartState, setResizeStartState] = useState<{ boxX: number; boxY: number; boxWidth: number; boxHeight: number; mouseX: number; mouseY: number } | null>(null);
  const justFinishedResizeRef = useRef(false);
  const liveBoxPosRef = useRef<{ id: string; x: number; y: number; width?: number; height?: number } | null>(null);
  const measureCtxRef = useRef<CanvasRenderingContext2D | null>(null);

  const measureLabelWidth = (text: string, fontSize: number, fontFamily: string) => {
    if (!measureCtxRef.current) {
      measureCtxRef.current = document.createElement('canvas').getContext('2d');
    }
    const ctx = measureCtxRef.current;
    if (!ctx) return fontSize * 0.6;
    ctx.font = `${fontSize}px ${fontFamily}`;
    return ctx.measureText(text || 'Label').width;
  };
  
  // Handle document-level mouse events for resize (ensures mouse up is always captured)
  useEffect(() => {
    if (!resizingBoxId) return;
    
    const handleDocumentMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current || !resizeStartState) return;
      
      const current = getCanvasCoordinates(e.clientX, e.clientY);
      
      const deltaX = (current.x - resizeStartState.mouseX);
      const deltaY = current.y - resizeStartState.mouseY;
      
      let newX = resizeStartState.boxX;
      let newY = resizeStartState.boxY;
      let newWidth = resizeStartState.boxWidth;
      let newHeight = resizeStartState.boxHeight;
      
      const minSize = 20;
      
      if (resizeHandle?.includes('e')) {
        newWidth = Math.max(minSize, resizeStartState.boxWidth + deltaX);
      }
      if (resizeHandle?.includes('w')) {
        const widthChange = resizeStartState.boxWidth - deltaX;
        if (widthChange >= minSize) {
          newWidth = widthChange;
          newX = resizeStartState.boxX + deltaX;
        }
      }
      if (resizeHandle?.includes('s')) {
        newHeight = Math.max(minSize, resizeStartState.boxHeight + deltaY);
      }
      if (resizeHandle?.includes('n')) {
        const heightChange = resizeStartState.boxHeight - deltaY;
        if (heightChange >= minSize) {
          newHeight = heightChange;
          newY = resizeStartState.boxY + deltaY;
        }
      }
      
      liveBoxPosRef.current = { id: resizingBoxId, x: newX, y: newY, width: newWidth, height: newHeight };
      const el = document.querySelector(`[data-box-id="${resizingBoxId}"]`) as HTMLElement | null;
      if (el) {
        el.style.left = `${newX}px`;
        el.style.top = `${newY}px`;
        el.style.width = `${newWidth}px`;
        el.style.height = `${newHeight}px`;
      }
    };
    
    const handleDocumentMouseUp = (e: MouseEvent) => {
      const live = liveBoxPosRef.current;
      if (live && live.width != null && live.height != null) {
        setBoxes(prev =>
          prev.map(box =>
            box.id === live.id ? { ...box, x: live.x, y: live.y, width: live.width!, height: live.height! } : box
          )
        );
      }
      // Clear resize state immediately, but keep box selected so handles remain visible
      const currentResizingBoxId = resizingBoxId;
      setResizingBoxId(null);
      setResizeHandle(null);
      setResizeStartState(null);
      
      // Mark that we just finished resizing to prevent canvas click from deselecting
      justFinishedResizeRef.current = true;
      setTimeout(() => {
        justFinishedResizeRef.current = false;
      }, 100);
      
      // Ensure box stays selected after resize (handles remain visible)
      if (currentResizingBoxId) {
        setSelectedBoxId(currentResizingBoxId);
        setShowBoxTools(true);
      }
      
      // Prevent this event from bubbling to canvas click handler
      e.stopPropagation();
      e.preventDefault();
      
    };
    
    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [resizingBoxId, resizeHandle, resizeStartState, canvasPanX, canvasPanY]);

  // Canvas labels
  const [labels, setLabels] = useState<Array<CanvasLabel & {
    isEditing?: boolean;
    originalText?: string; // Store original text when editing starts
  }>>([]);
  const [isLabelPlacementMode, setIsLabelPlacementMode] = useState(false);
  const [labelFont, setLabelFont] = useState<string>('system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif');
  const [labelFontSize, setLabelFontSize] = useState<number>(14);
  const [showLabelPalette, setShowLabelPalette] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [showLabelTools, setShowLabelTools] = useState(false);
  const [showZoomIndicator, setShowZoomIndicator] = useState(false);
  const [draggingLabelId, setDraggingLabelId] = useState<string | null>(null);
  const [labelDragOffset, setLabelDragOffset] = useState<{ dx: number; dy: number } | null>(null);
  const [resizingLabelId, setResizingLabelId] = useState<string | null>(null);
  const [labelResizeHandle, setLabelResizeHandle] = useState<'nw' | 'ne' | 'se' | 'sw' | null>(null);
  const [labelResizeStartState, setLabelResizeStartState] = useState<{ labelX: number; labelY: number; labelFontSize: number; mouseX: number; mouseY: number } | null>(null);
  const labelPaletteRef = useRef<HTMLDivElement>(null);
  const labelPaletteButtonRef = useRef<HTMLButtonElement>(null);
  const justFinishedLabelResizeRef = useRef(false);
  const justClickedLabelRef = useRef(false);
  const labelClickStartPos = useRef<{ x: number; y: number } | null>(null);
  const liveLabelPosRef = useRef<{ id: string; x: number; y: number; fontSize?: number } | null>(null);

  // Close label palette when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showLabelPalette) {
        const target = event.target as Node;
        // Don't close if clicking on the palette or the button
        if (
          labelPaletteRef.current && 
          !labelPaletteRef.current.contains(target) &&
          labelPaletteButtonRef.current &&
          !labelPaletteButtonRef.current.contains(target)
        ) {
          setShowLabelPalette(false);
        }
      }
    };
    
    if (showLabelPalette) {
      // Use a small delay to allow button's onClick to fire first
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showLabelPalette]);

  // Handle document-level mouse events for label resize (corner handles only, proportional resize)
  useEffect(() => {
    if (!resizingLabelId || !labelResizeHandle) return;
    
    const handleDocumentMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current || !labelResizeStartState) return;
      
      const label = labels.find(l => l.id === resizingLabelId);
      if (!label) return;
      
      // Get canvas coordinates for current mouse position
      const coords = getCanvasCoordinates(e.clientX, e.clientY);
      const currentMouseX = coords.x;
      const currentMouseY = coords.y;
      
      // Label padding constants
      const labelPaddingLeft = 8;
      const labelPaddingRight = 8;
      const labelPaddingTop = 4;
      const labelPaddingBottom = 4;
      const handleOffset = 1; // Handles are at -top-1, -left-1, etc.
      
      let startTextWidth = measureLabelWidth(label.text || 'Label', labelResizeStartState.labelFontSize, label.fontFamily);
      let startTextHeight = labelResizeStartState.labelFontSize * 1.2;
      
      // Mouse positions are already in canvas coordinates (stored that way in onMouseDown)
      const startMouseX = labelResizeStartState.mouseX;
      const startMouseY = labelResizeStartState.mouseY;
      
      // Calculate the anchor point (opposite corner - stays FIXED at original position)
      let anchorX: number, anchorY: number;
      if (labelResizeHandle === 'nw') {
        // Top-left dragged, anchor is bottom-right corner
        anchorX = labelResizeStartState.labelX + startTextWidth + labelPaddingLeft + labelPaddingRight;
        anchorY = labelResizeStartState.labelY + startTextHeight + labelPaddingTop + labelPaddingBottom;
      } else if (labelResizeHandle === 'ne') {
        // Top-right dragged, anchor is bottom-left corner
        anchorX = labelResizeStartState.labelX;
        anchorY = labelResizeStartState.labelY + startTextHeight + labelPaddingTop + labelPaddingBottom;
      } else if (labelResizeHandle === 'se') {
        // Bottom-right dragged, anchor is top-left corner
        anchorX = labelResizeStartState.labelX;
        anchorY = labelResizeStartState.labelY;
      } else { // sw
        // Bottom-left dragged, anchor is top-right corner
        anchorX = labelResizeStartState.labelX + startTextWidth + labelPaddingLeft + labelPaddingRight;
        anchorY = labelResizeStartState.labelY;
      }
      
      // Calculate original distance from anchor to start handle position
      const startDx = startMouseX - anchorX;
      const startDy = startMouseY - anchorY;
      const startDistance = Math.sqrt(startDx * startDx + startDy * startDy);
      
      // Calculate distance from anchor to current mouse position
      const currentDx = currentMouseX - anchorX;
      const currentDy = currentMouseY - anchorY;
      const currentDistance = Math.sqrt(currentDx * currentDx + currentDy * currentDy);
      
      // Calculate size ratio based on distance (no limits)
      const sizeRatio = startDistance > 0 ? currentDistance / startDistance : 1;
      const newSize = labelResizeStartState.labelFontSize * sizeRatio;
      
      const newTextWidth = measureLabelWidth(label.text || 'Label', newSize, label.fontFamily);
      const newTextHeight = newSize * 1.2;
      
      // Position label so the anchor stays FIXED at its original position
      // The anchor is the opposite corner - it must not move
      // The handle will naturally be positioned based on the size and anchor
      let newX: number, newY: number;
      if (labelResizeHandle === 'nw') {
        // Anchor (bottom-right) stays fixed at (anchorX, anchorY)
        // Label position: anchorX = label.x + newTextWidth + padding, anchorY = label.y + newTextHeight + padding
        newX = anchorX - newTextWidth - labelPaddingLeft - labelPaddingRight;
        newY = anchorY - newTextHeight - labelPaddingTop - labelPaddingBottom;
      } else if (labelResizeHandle === 'ne') {
        // Anchor (bottom-left) stays fixed at (anchorX, anchorY)
        // Label position: anchorX = label.x, anchorY = label.y + newTextHeight + padding
        newX = anchorX;
        newY = anchorY - newTextHeight - labelPaddingTop - labelPaddingBottom;
      } else if (labelResizeHandle === 'se') {
        // Anchor (top-left) stays fixed at (anchorX, anchorY)
        // Label position: anchorX = label.x, anchorY = label.y
        newX = anchorX;
        newY = anchorY;
      } else { // sw
        // Anchor (top-right) stays fixed at (anchorX, anchorY)
        // Label position: anchorX = label.x + newTextWidth + padding, anchorY = label.y
        newX = anchorX - newTextWidth - labelPaddingLeft - labelPaddingRight;
        newY = anchorY;
      }
      
      liveLabelPosRef.current = { id: resizingLabelId, x: newX, y: newY, fontSize: newSize };
      const el = document.querySelector(`[data-label-id="${resizingLabelId}"]`) as HTMLElement | null;
      if (el) {
        applyElementCanvasPos(el, newX, newY);
        el.style.fontSize = `${newSize}px`;
      }
    };
    
    const handleDocumentMouseUp = (e: MouseEvent) => {
      const live = liveLabelPosRef.current;
      if (live) {
        setLabels(prev =>
          prev.map(l =>
            l.id === live.id
              ? { ...l, x: live.x, y: live.y, fontSize: live.fontSize ?? l.fontSize }
              : l
          )
        );
      }
      const currentResizingLabelId = resizingLabelId;
      setResizingLabelId(null);
      setLabelResizeHandle(null);
      setLabelResizeStartState(null);
      
      justFinishedLabelResizeRef.current = true;
      setTimeout(() => {
        justFinishedLabelResizeRef.current = false;
      }, 100);
      
      if (currentResizingLabelId) {
        setSelectedLabelId(currentResizingLabelId);
        setShowLabelTools(true);
      }
      
      e.stopPropagation();
      e.preventDefault();
    };
    
    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleDocumentMouseMove);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, [resizingLabelId, labelResizeHandle, labelResizeStartState, labels, setLabels, setSelectedLabelId, setShowLabelTools]);
  
  const {
    currentProject,
    draggedItem,
    setDraggedItem,
    showAPRange,
    isCableRunMode,
    setCableRunMode,
    setCableRunRouting,
    selectedSourceEquipment,
    setSelectedSourceEquipment,
    createCableRun,
    setRackConfigOpen,
    setEquipmentConfigOpen,
    setCableConfigOpen,
    setSettingsConfigOpen,
    isRackPlacementMode,
    setSelectedEquipmentForPortView,
    setPortVisualizationOpen,
    gridSize,
    iconGridCells,
    showGrid,
    showLegend,
    showCanvasToolbar,
    showDesignInfoOverlay,
    setAddEquipmentOpen,
    setRackBuilderOpen,
    setSelectedRack,
    setHoveredEquipmentId,
    setHoveredCableId,
    setSelectedCable,
    setCablePortEditorOpen,
    removeEquipment,
    deleteCableRun,
    currency,
    showManufacturerInEquipmentList,
    getCurrentDesign,
    canvasMode,
    setCanvasMode,
    getFloorPlan,
    selectedFloorPlanWallId,
    selectedFloorPlanWallIds,
    selectedFloorPlanRoomId,
    selectedFloorPlanOpeningId,
    selectedReferenceImageId,
    selectFloorPlanWall,
    setSelectedFloorPlanRoomId,
    setSelectedFloorPlanOpeningId,
    setSelectedReferenceImageId,
    pendingReferenceImages,
    placeNextPendingReferenceImage,
    cancelPendingReferenceImages,
    canvasBackground,
    appTheme,
  } = useProjectStore(
    useShallow((s) => ({
      currentProject: s.currentProject,
      draggedItem: s.draggedItem,
      setDraggedItem: s.setDraggedItem,
      showAPRange: s.showAPRange,
      isCableRunMode: s.isCableRunMode,
      setCableRunMode: s.setCableRunMode,
      setCableRunRouting: s.setCableRunRouting,
      selectedSourceEquipment: s.selectedSourceEquipment,
      setSelectedSourceEquipment: s.setSelectedSourceEquipment,
      createCableRun: s.createCableRun,
      setRackConfigOpen: s.setRackConfigOpen,
      setEquipmentConfigOpen: s.setEquipmentConfigOpen,
      setCableConfigOpen: s.setCableConfigOpen,
      setSettingsConfigOpen: s.setSettingsConfigOpen,
      isRackPlacementMode: s.isRackPlacementMode,
      setSelectedEquipmentForPortView: s.setSelectedEquipmentForPortView,
      setPortVisualizationOpen: s.setPortVisualizationOpen,
      gridSize: s.gridSize,
      iconGridCells: s.iconGridCells,
      showGrid: s.showGrid,
      showLegend: s.showLegend,
      showCanvasToolbar: s.showCanvasToolbar,
      showDesignInfoOverlay: s.showDesignInfoOverlay,
      setAddEquipmentOpen: s.setAddEquipmentOpen,
      setRackBuilderOpen: s.setRackBuilderOpen,
      setSelectedRack: s.setSelectedRack,
      setHoveredEquipmentId: s.setHoveredEquipmentId,
      setHoveredCableId: s.setHoveredCableId,
      setSelectedCable: s.setSelectedCable,
      setCablePortEditorOpen: s.setCablePortEditorOpen,
      removeEquipment: s.removeEquipment,
      deleteCableRun: s.deleteCableRun,
      currency: s.currency,
      showManufacturerInEquipmentList: s.showManufacturerInEquipmentList,
      getCurrentDesign: s.getCurrentDesign,
      canvasMode: s.canvasMode,
      setCanvasMode: s.setCanvasMode,
      getFloorPlan: s.getFloorPlan,
      selectedFloorPlanWallId: s.selectedFloorPlanWallId,
      selectedFloorPlanWallIds: s.selectedFloorPlanWallIds,
      selectedFloorPlanRoomId: s.selectedFloorPlanRoomId,
      selectedFloorPlanOpeningId: s.selectedFloorPlanOpeningId,
      selectedReferenceImageId: s.selectedReferenceImageId,
      selectFloorPlanWall: s.selectFloorPlanWall,
      setSelectedFloorPlanRoomId: s.setSelectedFloorPlanRoomId,
      setSelectedFloorPlanOpeningId: s.setSelectedFloorPlanOpeningId,
      setSelectedReferenceImageId: s.setSelectedReferenceImageId,
      pendingReferenceImages: s.pendingReferenceImages,
      placeNextPendingReferenceImage: s.placeNextPendingReferenceImage,
      cancelPendingReferenceImages: s.cancelPendingReferenceImages,
      canvasBackground: s.canvasBackground,
      appTheme: s.appTheme,
    }))
  );
  const isDarkTheme = appTheme === 'dark';
  const logicMode = useProjectStore((s) => (s.getCurrentDesign()?.logicMode === 'logic' ? 'logic' : 'plan'));
  const logicResult = useLogicStore((s) => s.result);
  const logicHideIdle = useLogicStore((s) => s.hideIdle);
  const logicDrawerOpen = useLogicStore((s) => s.drawerOpen);
  const iconSize = canvasIconSizePx(gridSize, iconGridCells);
  const iconHalf = iconSize / 2;
  const nameBadgeW = NAME_BADGE_FALLBACK_WIDTH;
  const nameBadgeH = NAMEPLATE_ROW_PX;
  const nameBadgeHalfW = nameBadgeW / 2;
  const nameBadgeHalfH = nameBadgeH / 2;
  const currentDesign = getCurrentDesign();
  const floorPlan = getFloorPlan() || currentDesign?.floorPlan || null;

  const {
    draftStart,
    draftCurrent,
    draftSnap,
    snapMagnets,
    hoverRoomPreview,
    doorPreview,
    lengthInput,
    setLengthInput,
    roomNamePrompt,
    roomName,
    setRoomName,
    confirmRoomName,
    cancelRoomName,
    startRenameRoom,
    handleFloorPlanClick,
    handleFloorPlanMouseMove,
    handleWallPointerDown,
    consumeWallDragClick,
    isWallDragActive,
    commitTypedWallLength,
  } = useFloorPlanInteraction({ getCanvasCoordinates, canvasZoom });

  const {
    liveGeometry: liveReferenceGeometry,
    beginMove: beginReferenceMove,
    beginResize: beginReferenceResize,
    justFinishedTransform,
  } = useReferenceImageTransform({
    enabled: canvasMode === 'select',
    getCanvasCoordinates,
  });

  // Switch to place mode when dragging equipment / placing a rack
  useEffect(() => {
    if (draggedItem || isRackPlacementMode) {
      setCanvasMode('place');
    }
  }, [draggedItem, isRackPlacementMode, setCanvasMode]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      setCableRunMode(false);
      setCableRoutingPopoverOpen(false);
      setIsDrawingBox(false);
      setSelectedBoxStyle(null);
      setBoxStart(null);
      setBoxPreview(null);
      setShowBoxPalette(false);
      setIsLabelPlacementMode(false);
      setShowLabelPalette(false);
      cancelPendingReferenceImages();
      setDraggedItem(null);
      setCanvasMode('place');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setCableRunMode, setCanvasMode, cancelPendingReferenceImages, setDraggedItem]);

  useEffect(() => {
    if (!cableRoutingPopoverOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-cable-run-toolbar]')) return;
      setCableRoutingPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [cableRoutingPopoverOpen]);
  

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedItem) return;

    let position = getCanvasCoordinates(e.clientX, e.clientY);
    position = {
      x: position.x - nameBadgeHalfW,
      y: position.y - nameBadgeHalfH,
    };

    const { snapToGrid: shouldSnap, snapToGridTarget, gridSize: cell } = useProjectStore.getState();
    if (shouldSnap && (snapToGridTarget === 'icons' || snapToGridTarget === 'both') && cell > 0) {
      position = {
        x: Math.round(position.x / cell) * cell,
        y: Math.round(position.y / cell) * cell,
      };
    }

    if (draggedItem.type === 'equipment') {
      useProjectStore.getState().addEquipmentToCanvas(draggedItem.equipment, position);
    }

    setDraggedItem(null);
  }, [draggedItem, getCanvasCoordinates, nameBadgeHalfW, nameBadgeHalfH, setDraggedItem]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Panning & box drawing handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    try { window.getSelection()?.removeAllRanges(); } catch {}
    // Handle start of box drawing
    if (isDrawingBox && selectedBoxStyle && e.button === 0 && canvasRef.current) {
      const start = getCanvasCoordinates(e.clientX, e.clientY);
      setBoxStart(start);
      setBoxPreview({
        x: start.x,
        y: start.y,
        width: 0,
        height: 0,
        stroke: selectedBoxStyle.stroke,
        fill: selectedBoxStyle.fill
      });
      e.preventDefault();
      return;
    }

    // Check if clicking on equipment or other interactive elements
    const target = e.target as HTMLElement;
    const isClickingOnEquipment = target.closest('.react-draggable') || 
                                 target.closest('[data-equipment-id]') || 
                                 target.closest('[data-rack-id]') || 
                                 target.closest('[data-cable-id]') ||
                                 target.closest('.equipment-component') ||
                                 target.closest('.rack-component') ||
                                 target.closest('.cable-component') ||
                                 target.closest('.canvas-box') ||
                                 target.closest('.canvas-label') ||
                                 target.closest('[data-label-handle="resize"]') ||
                                 target.closest('div[style*="zIndex"]'); // Equipment has z-index styling
    
    const isClickingOnWallHandle = !!target.closest('[data-wall-handle]');
    const isClickingOnChrome = !!(
      target.closest('[data-design-browser]') ||
      target.closest('[data-logic-drawer]') ||
      target.closest('[data-floating-equipment-panel]') ||
      target.closest('[data-canvas-chrome]') ||
      target.closest('[data-floorplan-ui]')
    );

    const isFloorPlanDrawMode =
      canvasMode === 'wall' ||
      canvasMode === 'room' ||
      canvasMode === 'door' ||
      canvasMode === 'measure';

    // Middle-click always pans. Left-click pans from empty canvas OR floor-plan
    // walls/rooms/doors (so you aren't forced to find empty space). Corner handles
    // and selected-wall lengthen still stop propagation and won't reach here.
    // Cable-run mode still pans on empty space; clicks on equipment stay for linking.
    const isEmptyLeftClick =
      e.button === 0 &&
      !draggedItem &&
      !isRackPlacementMode &&
      pendingReferenceImages.length === 0 &&
      !isClickingOnEquipment &&
      !isClickingOnWallHandle &&
      !isClickingOnChrome &&
      !isDrawingBox &&
      !isFloorPlanDrawMode &&
      !isWallDragActive;

    if (e.button === 1 || isEmptyLeftClick) {
      lastPanPointRef.current = { x: e.clientX, y: e.clientY };
      didPanGestureRef.current = false;
      // Lock selection immediately so drag over panels/labels can't highlight text
      setPanTextSelectLock(true);
      attachPanDocListeners();
      if (e.button === 1) {
        isPanningRef.current = true;
        panPendingRef.current = false;
        setPanningCursor(true);
        setPanHoverLock(true);
        e.preventDefault();
      } else {
        panPendingRef.current = true;
        isPanningRef.current = false;
      }
      return;
    }
  }, [draggedItem, isRackPlacementMode, isDrawingBox, selectedBoxStyle, canvasMode, isWallDragActive, pendingReferenceImages.length]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Pan is driven by document listeners so it keeps working over portaled UI.
    if (isPanningRef.current || panPendingRef.current) {
      return;
    }

    if (draggingLabelId || draggingBoxId || resizingLabelId || resizingBoxId || useProjectStore.getState().isEquipmentDragActive) {
      // skip floor-plan hover while another drag is active
    } else if (handleFloorPlanMouseMove(e)) {
      return;
    }

    // Move an existing label while dragging (DOM only)
    if (draggingLabelId && labelDragOffset) {
      const layer = getPanningLayer();
      if (layer) {
        const mouse = clientToCanvas(e.clientX, e.clientY, layer.getBoundingClientRect(), canvasZoom);
        const newX = mouse.x - labelDragOffset.dx;
        const newY = mouse.y - labelDragOffset.dy;
        liveLabelPosRef.current = { id: draggingLabelId, x: newX, y: newY };
        applyElementCanvasPos(document.querySelector(`[data-label-id="${draggingLabelId}"]`) as HTMLElement | null, newX, newY);
      }
      return;
    }

    // Label resize is now handled by document-level event listeners in useEffect

    // Resize is now handled by document-level event listeners in useEffect
    // This prevents conflicts with canvas mouse events

    // Move an existing box while dragging (DOM only)
    if (draggingBoxId && boxDragOffset) {
      const layer = getPanningLayer();
      if (layer) {
        const mouse = clientToCanvas(e.clientX, e.clientY, layer.getBoundingClientRect(), canvasZoom);
        const newX = mouse.x - boxDragOffset.dx;
        const newY = mouse.y - boxDragOffset.dy;
        liveBoxPosRef.current = { id: draggingBoxId, x: newX, y: newY };
        applyElementCanvasPos(document.querySelector(`[data-box-id="${draggingBoxId}"]`) as HTMLElement | null, newX, newY);
      }
      return;
    }

    // Live update of box while drawing
    if (isDrawingBox && boxStart && canvasRef.current) {
      const current = getCanvasCoordinates(e.clientX, e.clientY);
      const x = Math.min(boxStart.x, current.x);
      const y = Math.min(boxStart.y, current.y);
      const width = Math.abs(current.x - boxStart.x);
      const height = Math.abs(current.y - boxStart.y);
      setBoxPreview(prev => prev ? { ...prev, x, y, width, height } : null);
      return;
    }

  }, [boxStart, boxDragOffset, canvasZoom, draggingBoxId, draggingLabelId, isDrawingBox, labelDragOffset, resizingLabelId, resizingBoxId, getCanvasCoordinates, handleFloorPlanMouseMove]);

  const handleMouseUp = useCallback(() => {
    // Finalize box drawing
    if (isDrawingBox && boxStart && boxPreview && selectedBoxStyle) {
      if (boxPreview.width > 4 && boxPreview.height > 4) {
        const newId = `box-${++boxIdRef.current}`;
        setBoxes(prev => [
          ...prev,
          {
            id: newId,
            x: boxPreview.x,
            y: boxPreview.y,
            width: boxPreview.width,
            height: boxPreview.height,
            stroke: selectedBoxStyle.stroke,
            fill: selectedBoxStyle.fill,
            patternColor: selectedBoxStyle.fill ? selectedBoxStyle.stroke : undefined
          }
        ]);
      }
      setBoxStart(null);
      setBoxPreview(null);
      setIsDrawingBox(false);
      setSelectedBoxStyle(null);
      return;
    }

    // Stop resizing a box if active
    if (resizingBoxId) {
      setResizingBoxId(null);
      setResizeHandle(null);
      setResizeStartState(null);
    }

    // Stop moving a box if active
    if (draggingBoxId) {
      const live = liveBoxPosRef.current;
      if (live && live.id === draggingBoxId) {
        setBoxes(prev => prev.map(box => box.id === live.id ? { ...box, x: live.x, y: live.y } : box));
      }
      setDraggingBoxId(null);
      setBoxDragOffset(null);
    }

    // Stop moving a label if active
    if (draggingLabelId) {
      const live = liveLabelPosRef.current;
      if (live && live.id === draggingLabelId) {
        setLabels(prev => prev.map(label => label.id === live.id ? { ...label, x: live.x, y: live.y } : label));
      }
      const currentDraggingLabelId = draggingLabelId;
      setDraggingLabelId(null);
      setLabelDragOffset(null);
      // Always keep label selected after drag/click ends
      if (currentDraggingLabelId) {
        setSelectedLabelId(currentDraggingLabelId);
        setShowLabelTools(true);
        // Mark that we just finished interacting with a label
        justClickedLabelRef.current = true;
        setTimeout(() => {
          justClickedLabelRef.current = false;
        }, 100);
      }
      labelClickStartPos.current = null;
    }

    // Stop resizing a label if active
    if (resizingLabelId) {
      setResizingLabelId(null);
      setLabelResizeHandle(null);
      setLabelResizeStartState(null);
    }

    finishPan();
  }, [boxPreview, boxStart, draggingBoxId, draggingLabelId, isDrawingBox, resizingLabelId, selectedBoxStyle]);

  // Stop panning when cursor leaves the canvas area
  const handleMouseLeave = useCallback(() => {
    if (isDrawingBox) {
      setBoxStart(null);
      setBoxPreview(null);
    }
    if (draggingBoxId) {
      const live = liveBoxPosRef.current;
      if (live && live.id === draggingBoxId) {
        setBoxes(prev => prev.map(box => box.id === live.id ? { ...box, x: live.x, y: live.y } : box));
      }
      setDraggingBoxId(null);
      setBoxDragOffset(null);
    }
    if (draggingLabelId) {
      const live = liveLabelPosRef.current;
      if (live && live.id === draggingLabelId) {
        setLabels(prev => prev.map(label => label.id === live.id ? { ...label, x: live.x, y: live.y } : label));
      }
      setDraggingLabelId(null);
      setLabelDragOffset(null);
    }
    if (resizingLabelId) {
      setResizingLabelId(null);
      setLabelResizeHandle(null);
      setLabelResizeStartState(null);
    }
    // Do not finishPan here: floating panels are portaled outside the canvas,
    // and ending pan on leave restores text selection while the button is still down.
  }, [draggingBoxId, draggingLabelId, isDrawingBox, resizingLabelId]);

  // Helper function to find equipment at click (prefer exact hit, fallback to nearest within small radius)
  const findNearestEquipment = useCallback((clickPos: { x: number; y: number }, snapDistance = 15): PlacedEquipment | null => {
    const design = getCurrentDesign();
    if (!currentProject || !design) return null;

    const layer = getPanningLayer();
    const layerRect = layer?.getBoundingClientRect();
    const zoom = canvasZoom || 1;
    const pointInDom = (el: Element | null) => {
      if (!el || !layerRect) return false;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return false;
      const x = (r.left - layerRect.left) / zoom;
      const y = (r.top - layerRect.top) / zoom;
      const w = r.width / zoom;
      const h = r.height / zoom;
      return clickPos.x >= x && clickPos.x <= x + w && clickPos.y >= y && clickPos.y <= y + h;
    };

    const hit = design.equipment.find((eq) => {
      if (
        clickPos.x >= eq.position.x &&
        clickPos.x <= eq.position.x + nameBadgeW &&
        clickPos.y >= eq.position.y &&
        clickPos.y <= eq.position.y + nameBadgeH * 2
      ) {
        return true;
      }
      return (
        pointInDom(document.querySelector(`[data-equipment-id="${eq.id}"]`)) ||
        pointInDom(document.querySelector(`[data-port-stage-for="${eq.id}"]`)) ||
        pointInDom(document.querySelector(`[data-equipment-id="${eq.id}"] [data-equipment-nameplate]`))
      );
    });
    if (hit) return hit;

    let nearestEquipment: PlacedEquipment | null = null;
    let minDistance = snapDistance;

    design.equipment.forEach((equipment) => {
      const equipmentCenter = {
        x: equipment.position.x + nameBadgeHalfW,
        y: equipment.position.y + nameBadgeHalfH
      };
      const distance = Math.hypot(
        clickPos.x - equipmentCenter.x,
        clickPos.y - equipmentCenter.y
      );
      if (distance < minDistance) {
        nearestEquipment = equipment;
        minDistance = distance;
      }
    });

    return nearestEquipment;
  }, [currentProject, nameBadgeW, nameBadgeH, nameBadgeHalfW, nameBadgeHalfH, canvasZoom, getCurrentDesign]);

  const [noPortsDialog, setNoPortsDialog] = useState<{name: string} | null>(null);
  const [sameDeviceDialog, setSameDeviceDialog] = useState<{name: string} | null>(null);
  const [shakeRect, setShakeRect] = useState<{ left: number; top: number; } | null>(null);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    try {
      // Ignore generic canvas click handling while drawing boxes
      if (isDrawingBox) {
        return;
      }

      // A real pan drag should not also select / start cable links
      if (consumePanGestureClick()) {
        return;
      }

      // Don't treat clicks on boxes/labels or their tools menus as "empty canvas" clicks
      const target = e.target as HTMLElement;
      if (
        target.closest('.canvas-box') ||
        target.closest('.box-tools-menu') ||
        target.closest('.canvas-label') ||
        target.closest('.label-tools-menu') ||
        target.closest('.box-resize-handle') ||
        target.closest('.label-resize-handle') ||
        target.closest('[data-floorplan-ui]') ||
        target.closest('[data-design-browser]') ||
        justFinishedResizeRef.current ||
        justFinishedLabelResizeRef.current ||
        justClickedLabelRef.current
      ) {
        return;
      }

      // Floor plan draw / measure modes consume the click
      if (handleFloorPlanClick(e)) {
        return;
      }

      const clickPosition = getCanvasCoordinates(e.clientX, e.clientY);

      // Place next queued reference image (click-to-place, like equipment)
      if (pendingReferenceImages.length > 0) {
        placeNextPendingReferenceImage(clickPosition);
        return;
      }

      // Handle label placement mode
      if (isLabelPlacementMode) {
        const newId = `label-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const baseText = 'Label'; // Start with visible default text
        setLabels(prev => [
          ...prev,
          {
            id: newId,
            x: clickPosition.x,
            y: clickPosition.y,
            text: baseText,
            fontFamily: labelFont,
            fontSize: labelFontSize,
            isEditing: true,
            originalText: baseText // Store original for cancel
          }
        ]);
        setIsLabelPlacementMode(false);
        setSelectedLabelId(newId);
        setShowLabelTools(true);
        return;
      }

      // Handle cable run mode
      if (isCableRunMode) {
        const nearestEquipment = findNearestEquipment(clickPosition);
        
        if (nearestEquipment) {
          // If source is a specific port and destination has multiple ports, open destination port picker
          if (!selectedSourceEquipment) {
            // First click - verify open ports and either select single open port or prompt for selection
            const portCount = getEquipmentCablePortCount(nearestEquipment);
            const usedSet = new Set<number>();
            // Read cables from design-scoped location
            const design = getCurrentDesign();
            (design?.cables || []).forEach(c => {
              const m1 = c.from.match(/^(.+):port:(\d+)$/);
              const m2 = c.to.match(/^(.+):port:(\d+)$/);
              if (m1 && m1[1] === nearestEquipment.id) usedSet.add(parseInt(m1[2]));
              if (m2 && m2[1] === nearestEquipment.id) usedSet.add(parseInt(m2[2]));
            });
            const openPorts = Array.from({ length: portCount }, (_, i) => i + 1).filter(n => !usedSet.has(n));
            if (portCount > 0 && openPorts.length === 0) {
              setNoPortsDialog({ name: nearestEquipment.equipment.name });
              return;
            }
            if (openPorts.length === 1) {
              // Set source as specific port id so downstream creation can proceed
              const sourcePortId = `${nearestEquipment.id}:port:${openPorts[0]}`;
              setSelectedSourceEquipment({ ...nearestEquipment, id: sourcePortId } as any);
              return;
            }
            // Multiple open ports: open port picker for source
            setSelectedEquipmentForPortView(nearestEquipment);
            setPortVisualizationOpen(true);
          } else if (selectedSourceEquipment.id !== nearestEquipment.id) {
            // Disallow connecting a device to itself (even if source is a specific port id)
            const m = String(selectedSourceEquipment.id).match(/^(.+?):port:/);
            const sourceEquipId = m ? m[1] : selectedSourceEquipment.id;
            if (sourceEquipId === nearestEquipment.id) {
              setSameDeviceDialog({ name: nearestEquipment.equipment.name });
              // Trigger shake on the equipment box
              setShakeRect({ left: nearestEquipment.position.x - 2, top: nearestEquipment.position.y - 2 });
              setTimeout(() => setShakeRect(null), 500);
              return;
            }
            // Second click: choose destination, ensure open port exists
            const portCount = getEquipmentCablePortCount(nearestEquipment);
            const usedSet = new Set<number>();
            // Read cables from design-scoped location
            const design = getCurrentDesign();
            (design?.cables || []).forEach(c => {
              const m1 = c.from.match(/^(.+):port:(\d+)$/);
              const m2 = c.to.match(/^(.+):port:(\d+)$/);
              if (m1 && m1[1] === nearestEquipment.id) usedSet.add(parseInt(m1[2]));
              if (m2 && m2[1] === nearestEquipment.id) usedSet.add(parseInt(m2[2]));
            });
            const openPorts = Array.from({ length: portCount }, (_, i) => i + 1).filter(n => !usedSet.has(n));
            if (openPorts.length === 0) {
              setNoPortsDialog({ name: nearestEquipment.equipment.name });
              return;
            }
            const sourceIsPort = typeof selectedSourceEquipment.id === 'string' && selectedSourceEquipment.id.includes(':port:');
            if (!sourceIsPort) {
              // Source was equipment (user picked port in modal); wait for source to pick a port
              setSelectedEquipmentForPortView(selectedSourceEquipment);
              setPortVisualizationOpen(true);
              return;
            }
            if (openPorts.length === 1) {
              const destPortId = `${nearestEquipment.id}:port:${openPorts[0]}`;
              console.log('Canvas - Creating cable between ports:', selectedSourceEquipment.id, '->', destPortId);
              createCableRun(selectedSourceEquipment.id, destPortId, 'ethernet');
              setSelectedSourceEquipment(null);
              return;
            }
            // Multiple open ports: open port picker for destination
            setSelectedEquipmentForPortView(nearestEquipment);
            setPortVisualizationOpen(true);
          }
        }
        return;
      }

      // Handle rack placement mode
      if (isRackPlacementMode) {
        let rackPos = {
          x: clickPosition.x - iconHalf,
          y: clickPosition.y - iconHalf,
        };
        const { snapToGrid: shouldSnap, snapToGridTarget, gridSize: cell } = useProjectStore.getState();
        if (shouldSnap && (snapToGridTarget === 'icons' || snapToGridTarget === 'both') && cell > 0) {
          rackPos = {
            x: Math.round(rackPos.x / cell) * cell,
            y: Math.round(rackPos.y / cell) * cell,
          };
        }
        useProjectStore.getState().placeRackAtPosition(rackPos);
        return;
      }

      // Handle equipment placement mode
      if (draggedItem) {
        if (draggedItem.type === 'equipment') {
          let position = {
            x: clickPosition.x - nameBadgeHalfW,
            y: clickPosition.y - nameBadgeHalfH,
          };
          const { snapToGrid: shouldSnap, snapToGridTarget, gridSize: cell } = useProjectStore.getState();
          if (shouldSnap && (snapToGridTarget === 'icons' || snapToGridTarget === 'both') && cell > 0) {
            position = {
              x: Math.round(position.x / cell) * cell,
              y: Math.round(position.y / cell) * cell,
            };
          }
          useProjectStore.getState().addEquipmentToCanvas(draggedItem.equipment, position);
        }

        setDraggedItem(null);
        return;
      }

      // Close sidebar when clicking on empty space
      selectFloorPlanWall(null);
      setSelectedFloorPlanRoomId(null);
      setSelectedFloorPlanOpeningId(null);
      if (!justFinishedTransform()) {
        setSelectedReferenceImageId(null);
      }
      setRackConfigOpen(false);
      setEquipmentConfigOpen(false);
      setCableConfigOpen(false);
      // Only close settings if not pinned
      const { isSettingsPinned } = useProjectStore.getState();
      if (!isSettingsPinned) {
        setSettingsConfigOpen(false);
      }

      // Clicking on empty canvas clears box/label selection & tool modes
      // But don't clear if we just finished resizing (let user click outside to deselect)
      if (!justFinishedResizeRef.current) {
      setSelectedBoxId(null);
      setShowBoxTools(false);
      }
      // Cancel any label editing when clicking outside - revert to original text
      setLabels(prev =>
        prev.map(l => {
          if (!l.isEditing) return l;
          const revertedText = l.originalText !== undefined 
            ? l.originalText 
            : (l.text.trim() || 'Label');
          return { ...l, isEditing: false, text: revertedText, originalText: undefined };
        })
      );
      setSelectedLabelId(null);
      setShowLabelTools(false);
    } catch (error) {
      console.error('Error handling canvas click:', error);
    }
  }, [createCableRun, draggedItem, findNearestEquipment, iconHalf, isCableRunMode, isLabelPlacementMode, isRackPlacementMode, isDrawingBox, labelFont, labelFontSize, selectedSourceEquipment, setCableConfigOpen, setDraggedItem, setRackConfigOpen, setSelectedEquipmentForPortView, setSelectedSourceEquipment, setSettingsConfigOpen, setPortVisualizationOpen, setLabels, getCanvasCoordinates, handleFloorPlanClick, selectFloorPlanWall, setSelectedFloorPlanRoomId, setSelectedFloorPlanOpeningId, pendingReferenceImages, placeNextPendingReferenceImage, justFinishedTransform, setSelectedReferenceImageId]);

  // Debug floorplan state
  useEffect(() => {
    if (currentProject) {
      console.log('Canvas floorplan state changed:', currentProject.floorplan);
      if (currentProject.floorplan) {
        console.log('Floorplan exists - should be visible');
      } else {
        console.log('Floorplan is null/undefined - not visible');
      }
    }
  }, [currentProject?.floorplan]);

  const designForRender = getCurrentDesign();
  const cablesForRender = uniqueCablesById(designForRender?.cables);

  // Load boxes from design when design changes
  useEffect(() => {
    skipBoxPersistRef.current = true;
    if (designForRender?.boxes) {
      setBoxes(designForRender.boxes);
      // Update boxIdRef to avoid ID conflicts
      if (designForRender.boxes.length > 0) {
        const maxId = Math.max(...designForRender.boxes.map(b => {
          const match = b.id.match(/box-(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        }));
        boxIdRef.current = maxId;
      }
    } else {
      setBoxes([]);
    }
  }, [designForRender?.id]);

  // Load labels from design when design changes
  useEffect(() => {
    skipLabelPersistRef.current = true;
    if (designForRender?.labels) {
      setLabels(designForRender.labels.map(label => ({
        ...label,
        isEditing: false,
        originalText: undefined
      })));
    } else {
      setLabels([]);
    }
  }, [designForRender?.id]);
  
  const persistBoxesToStore = useCallback((nextBoxes: CanvasBox[]) => {
    const state = useProjectStore.getState();
    const project = state.currentProject;
    const design = state.getCurrentDesign();
    if (!project || !design) return;
    useProjectStore.setState({
      currentProject: {
        ...project,
        designs: (project.designs || []).map((d) =>
          d.id === design.id ? { ...d, boxes: nextBoxes, updatedAt: new Date() } : d
        ),
        updatedAt: new Date(),
      },
    });
  }, []);

  const persistLabelsToStore = useCallback((nextLabels: Array<CanvasLabel & { isEditing?: boolean; originalText?: string }>) => {
    const state = useProjectStore.getState();
    const project = state.currentProject;
    const design = state.getCurrentDesign();
    if (!project || !design) return;
    const labelsToSave: CanvasLabel[] = nextLabels.map(({ isEditing, originalText, ...label }) => label);
    useProjectStore.setState({
      currentProject: {
        ...project,
        designs: (project.designs || []).map((d) =>
          d.id === design.id ? { ...d, labels: labelsToSave, updatedAt: new Date() } : d
        ),
        updatedAt: new Date(),
      },
    });
  }, []);

  const skipBoxPersistRef = useRef(true);
  const skipLabelPersistRef = useRef(true);

  useEffect(() => {
    if (skipBoxPersistRef.current) {
      skipBoxPersistRef.current = false;
      return;
    }
    if (draggingBoxId || resizingBoxId) return;
    persistBoxesToStore(boxes);
  }, [boxes, draggingBoxId, resizingBoxId, persistBoxesToStore]);

  useEffect(() => {
    if (skipLabelPersistRef.current) {
      skipLabelPersistRef.current = false;
      return;
    }
    if (draggingLabelId || resizingLabelId) return;
    persistLabelsToStore(labels);
  }, [labels, draggingLabelId, resizingLabelId, persistLabelsToStore]);

  // Calculate bounding box of all objects (equipment, racks, cable endpoints)
  const calculateBoundingBox = useCallback(() => {
    if (!designForRender) return null;
    
    const draggedEquipmentPositions = useProjectStore.getState().draggedEquipmentPositions;
    const bounds: { minX: number; maxX: number; minY: number; maxY: number }[] = [];
    
    // Add equipment bounds (compact name badges)
    // Use live dragged positions if available, otherwise use stored positions
    designForRender.equipment.forEach(eq => {
      const livePos = draggedEquipmentPositions[eq.id] || eq.position;
      bounds.push({
        minX: livePos.x,
        maxX: livePos.x + nameBadgeW,
        minY: livePos.y,
        maxY: livePos.y + nameBadgeH * 2
      });
    });
    
    // Add rack bounds (racks are typically 60x(rack.size * 19) pixels)
    designForRender.racks.forEach(rack => {
      const rackHeight = rack.size * 19; // Each U is 19px
      const rackWidth = 60;
      bounds.push({
        minX: rack.position.x,
        maxX: rack.position.x + rackWidth,
        minY: rack.position.y,
        maxY: rack.position.y + rackHeight
      });
    });
    
    // Add cable endpoint positions (approximate from equipment positions)
    uniqueCablesById(designForRender.cables).forEach(cable => {
      // Extract equipment IDs from cable endpoints
      const fromId = cable.from.split(':')[0];
      const toId = cable.to.split(':')[0];
      
      const fromEq = designForRender.equipment.find(eq => eq.id === fromId);
      const toEq = designForRender.equipment.find(eq => eq.id === toId);
      
      // Add equipment center points for cables (use live positions if available)
      if (fromEq) {
        const livePos = draggedEquipmentPositions[fromEq.id] || fromEq.position;
        bounds.push({
          minX: livePos.x + nameBadgeHalfW,
          maxX: livePos.x + nameBadgeHalfW,
          minY: livePos.y + nameBadgeHalfH,
          maxY: livePos.y + nameBadgeHalfH
        });
      }
      if (toEq) {
        const livePos = draggedEquipmentPositions[toEq.id] || toEq.position;
        bounds.push({
          minX: livePos.x + nameBadgeHalfW,
          maxX: livePos.x + nameBadgeHalfW,
          minY: livePos.y + nameBadgeHalfH,
          maxY: livePos.y + nameBadgeHalfH
        });
      }
    });
    
    if (bounds.length === 0) return null;
    
    const minX = Math.min(...bounds.map(b => b.minX));
    const maxX = Math.max(...bounds.map(b => b.maxX));
    const minY = Math.min(...bounds.map(b => b.minY));
    const maxY = Math.max(...bounds.map(b => b.maxY));
    
    // Add padding
    const padding = 100;
    return {
      minX: minX - padding,
      maxX: maxX + padding,
      minY: minY - padding,
      maxY: maxY + padding,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2
    };
  }, [designForRender, nameBadgeW, nameBadgeH, nameBadgeHalfW, nameBadgeHalfH]);

  // Recenter objects: center the bounding box in the viewport
  // The panning layer is positioned at (canvasPanX, canvasPanY), so an object at canvas position (x, y)
  // appears at viewport position (x + canvasPanX, y + canvasPanY)
  // To center an object at canvas position (x, y): x + canvasPanX = viewportWidth/2
  // Therefore: canvasPanX = viewportWidth/2 - x
  const handleRecenter = useCallback(() => {
    const bbox = calculateBoundingBox();
    if (!bbox || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const viewportCenterX = rect.width / 2;
    const viewportCenterY = rect.height / 2;
    
    // Calculate pan to center the bounding box center at viewport center
    const newPanX = viewportCenterX - bbox.centerX;
    const newPanY = viewportCenterY - bbox.centerY;
    
    setCanvasPan(newPanX, newPanY);
  }, [calculateBoundingBox, setCanvasPan]);

  // View objects: adjust pan to show all objects in viewport (fit to view)
  const handleViewObjects = useCallback(() => {
    const bbox = calculateBoundingBox();
    if (!bbox || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const viewportWidth = rect.width;
    const viewportHeight = rect.height;
    
    // Always center the bounding box in the viewport
    const newPanX = viewportWidth / 2 - bbox.centerX;
    const newPanY = viewportHeight / 2 - bbox.centerY;
    
    setCanvasPan(newPanX, newPanY);
  }, [calculateBoundingBox, setCanvasPan]);

  // Zoom: 50% … 150%
  const zoomLevels = CANVAS_ZOOM_LEVELS as unknown as number[];
  
  const handleZoomIn = useCallback((mouseX?: number, mouseY?: number) => {
    const currentIndex = zoomLevels.indexOf(canvasZoom);
    if (currentIndex < zoomLevels.length - 1) {
      const newZoom = zoomLevels[currentIndex + 1];
      // If mouse position is provided, zoom towards it
      if (mouseX !== undefined && mouseY !== undefined && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const mouseCanvasX = (mouseX - rect.left - canvasPanX) / canvasZoom;
        const mouseCanvasY = (mouseY - rect.top - canvasPanY) / canvasZoom;
        
        // Calculate new pan to keep mouse point fixed
        const newPanX = mouseX - rect.left - mouseCanvasX * newZoom;
        const newPanY = mouseY - rect.top - mouseCanvasY * newZoom;
        
        setCanvasPan(newPanX, newPanY);
      }
      setCanvasZoom(newZoom);
      // Show zoom indicator
      setShowZoomIndicator(true);
    }
  }, [canvasZoom, canvasPanX, canvasPanY, setCanvasZoom, setCanvasPan]);

  const handleZoomOut = useCallback((mouseX?: number, mouseY?: number) => {
    const currentIndex = zoomLevels.indexOf(canvasZoom);
    if (currentIndex > 0) {
      const newZoom = zoomLevels[currentIndex - 1];
      // If mouse position is provided, zoom towards it
      if (mouseX !== undefined && mouseY !== undefined && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const mouseCanvasX = (mouseX - rect.left - canvasPanX) / canvasZoom;
        const mouseCanvasY = (mouseY - rect.top - canvasPanY) / canvasZoom;
        
        // Calculate new pan to keep mouse point fixed
        const newPanX = mouseX - rect.left - mouseCanvasX * newZoom;
        const newPanY = mouseY - rect.top - mouseCanvasY * newZoom;
        
        setCanvasPan(newPanX, newPanY);
      }
      setCanvasZoom(newZoom);
      // Show zoom indicator
      setShowZoomIndicator(true);
    }
  }, [canvasZoom, canvasPanX, canvasPanY, setCanvasZoom, setCanvasPan]);

  const handleZoomToLevel = useCallback((level: number) => {
    setCanvasZoom(level);
  }, [setCanvasZoom]);

  // Mouse wheel zoom support (no modifier keys needed) - zoom towards mouse cursor
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // Let overlays (browser, logic drawer, floating panel, chrome) scroll instead of zooming
      const target = e.target as HTMLElement | null;
      if (
        target?.closest?.(
          '[data-design-browser], [data-logic-drawer], [data-logic-drawer-tab], [data-floating-equipment-panel], [data-canvas-chrome], [data-floorplan-ui]'
        )
      ) {
        return;
      }
      // Prevent default scrolling when zooming
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn(e.clientX, e.clientY);
      } else {
        handleZoomOut(e.clientX, e.clientY);
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        canvas.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleZoomIn, handleZoomOut]);

  // Show zoom indicator when zoom changes
  useEffect(() => {
    setShowZoomIndicator(true);
  }, [canvasZoom]);

  // Auto-hide zoom indicator after 1 second
  useEffect(() => {
    if (showZoomIndicator) {
      const timer = setTimeout(() => {
        setShowZoomIndicator(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [showZoomIndicator, canvasZoom]);

  if (!currentProject || !designForRender) {
    return (
      <div className={`flex-1 flex items-center justify-center ${isDarkTheme ? 'bg-[#1a1d22]' : 'bg-white'}`}>
        <div className="text-center">
          <h2 className={`text-2xl font-semibold mb-4 ${isDarkTheme ? 'text-gray-200' : 'text-gray-600'}`}>No Collection Loaded</h2>
          <p className={isDarkTheme ? 'text-gray-400' : 'text-gray-500'}>Create a new collection to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={canvasRef}
      data-canvas-viewport="true"
      className={`flex-1 relative overflow-hidden select-none ${
        isDarkTheme
          ? (canvasBackground === 'dark' ? 'bg-[#111317]' : 'bg-[#1a1d22]')
          : (canvasBackground === 'dark' ? 'bg-slate-950' : 'bg-white')
      } ${
        draggedItem || pendingReferenceImages.length > 0 ? 'cursor-crosshair' :
        isDrawingBox ? 'cursor-crosshair' :
        isRackPlacementMode || canvasMode === 'wall' || canvasMode === 'door' || canvasMode === 'measure' || canvasMode === 'room'
          ? 'cursor-crosshair'
          : ''
      }`}
      style={{ backgroundImage: 'none' }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={handleCanvasClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {showGrid && (
        <div
          ref={gridOverlayRef}
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            ...canvasGridStyle(
              gridSize,
              canvasZoom,
              isDarkTheme || canvasBackground === 'dark'
            ),
          }}
        />
      )}
      {/* Background Layer - Pans with the canvas (very large area anchored at top-left) */}
      <div
        ref={panningLayerRef}
        className="absolute"
        data-panning-layer="true"
        style={{
          width: 100000,
          height: 100000,
          backgroundImage: 'none',
          transform: `scale(${canvasZoom})`,
          transformOrigin: 'top left',
        }}
      >
        {/* Legacy raster floorplan (fallback if no live floor plan / reference) */}
        {currentProject.floorplan &&
          !(floorPlan && getFloorPlanReferenceImages(floorPlan).length > 0) &&
          !floorPlan?.walls?.length && (
          <FloorplanImage floorplan={currentProject.floorplan} />
        )}

        {/* Live vector floor plan (walls / rooms / doors) */}
        {floorPlan && (
          <FloorPlanLayer
            floorPlan={floorPlan}
            selectedWallId={selectedFloorPlanWallId}
            selectedWallIds={selectedFloorPlanWallIds}
            selectedRoomId={selectedFloorPlanRoomId}
            selectedOpeningId={selectedFloorPlanOpeningId}
            selectedReferenceImageId={selectedReferenceImageId}
            referenceImagesInteractive={canvasMode === 'select'}
            liveReferenceGeometry={liveReferenceGeometry}
            onReferenceImageDoubleClick={(imageId) => {
              setSelectedReferenceImageId(imageId);
              if (canvasMode !== 'select') setCanvasMode('select');
            }}
            onReferenceImageMoveStart={beginReferenceMove}
            onReferenceResizeStart={beginReferenceResize}
            hoverRoomPreview={hoverRoomPreview}
            doorPreview={doorPreview}
            onSelectWall={(wallId, e) => {
              if (consumeWallDragClick() || consumePanGestureClick()) return;
              selectFloorPlanWall(wallId, !!e?.shiftKey);
            }}
            onSelectRoom={(roomId) => {
              if (consumePanGestureClick()) return;
              setSelectedFloorPlanRoomId(roomId);
            }}
            onSelectOpening={(openingId) => {
              if (consumePanGestureClick()) return;
              setSelectedFloorPlanOpeningId(openingId);
            }}
            onRenameRoom={startRenameRoom}
            onWallPointerDown={handleWallPointerDown}
            onCommitWallLength={commitTypedWallLength}
            showHandles={canvasMode === 'select'}
            interactive={canvasMode === 'select' || canvasMode === 'room'}
          />
        )}

        {floorPlan && (draftStart || draftCurrent || snapMagnets.length > 0 || (canvasMode === 'wall' && draftSnap)) && (
          <WallDraftPreview
            start={draftStart}
            current={draftCurrent}
            snap={draftSnap}
            magnets={snapMagnets}
            pixelsPerMeter={DEFAULT_PIXELS_PER_METER}
            units={floorPlan.units}
            lengthInput={lengthInput}
            onLengthInputChange={setLengthInput}
            measureOnly={canvasMode === 'measure'}
            canvasZoom={canvasZoom}
          />
        )}

        {/* Racks (from current design) */}
        {designForRender.racks
          .filter((rack) => isVisibleOnCanvas(designForRender, 'rack', rack))
          .map((rack) => (
          <RackComponent key={rack.id} rack={rack} />
        ))}

        {/* Equipment (from current design) */}
        {designForRender.equipment
          .filter((equipment) =>
            isVisibleOnCanvas(designForRender, 'equipment', equipment, equipment.equipment.type)
          )
          .map((equipment) => (
          <EquipmentComponent key={equipment.id} equipment={equipment} />
        ))}

        {Object.entries(collabGhosts).map(([id, pos]) => {
          const source = designForRender.equipment.find((eq) => eq.id === id);
          return (
            <div
              key={`ghost-${id}`}
              className="pointer-events-none absolute flex items-center justify-center rounded-md border-2 border-dashed border-sky-400 bg-sky-400/20 text-[10px] font-medium text-sky-700"
              style={{ left: pos.x, top: pos.y, zIndex: 40, width: nameBadgeW, height: nameBadgeH, padding: '0 4px' }}
            >
              {source?.equipment.name?.slice(0, 8) || '…'}
            </div>
          );
        })}

        {/* Cable Runs — canvas overlay of straight lines */}
        <CablesLayer
          cables={cablesForRender.filter((c) => isVisibleOnCanvas(designForRender, 'cable', c))}
          equipment={designForRender.equipment}
          racks={designForRender.racks}
        />

        {/* AP Range Visualization */}
        <APRangeVisualization
          equipment={designForRender.equipment}
          showRange={showAPRange}
        />
        {/* Simple canvas boxes */}
        {boxes.filter((box) => isVisibleOnCanvas(designForRender, 'box', box)).map(box => {
          const isSelected = selectedBoxId === box.id;
          const isShaded = !!box.patternColor;
          const patternColor = box.patternColor || box.stroke;
          const handleSize = 8;
          
          // Resize handles positions
          const handles: Array<{ pos: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'; x: number; y: number; cursor: string }> = [
            { pos: 'nw', x: -handleSize / 2, y: -handleSize / 2, cursor: 'nw-resize' },
            { pos: 'n', x: box.width / 2 - handleSize / 2, y: -handleSize / 2, cursor: 'n-resize' },
            { pos: 'ne', x: box.width - handleSize / 2, y: -handleSize / 2, cursor: 'ne-resize' },
            { pos: 'e', x: box.width - handleSize / 2, y: box.height / 2 - handleSize / 2, cursor: 'e-resize' },
            { pos: 'se', x: box.width - handleSize / 2, y: box.height - handleSize / 2, cursor: 'se-resize' },
            { pos: 's', x: box.width / 2 - handleSize / 2, y: box.height - handleSize / 2, cursor: 's-resize' },
            { pos: 'sw', x: -handleSize / 2, y: box.height - handleSize / 2, cursor: 'sw-resize' },
            { pos: 'w', x: -handleSize / 2, y: box.height / 2 - handleSize / 2, cursor: 'w-resize' },
          ];
          
          return (
            <div key={box.id}>
            <div
                className="absolute rounded-xl canvas-box"
                data-box-id={box.id}
                style={{
                left: box.x,
                top: box.y,
                width: box.width,
                height: box.height,
                borderWidth: 2,
                borderStyle: 'dashed',
                  borderColor: box.stroke,
                backgroundColor: isShaded ? 'transparent' : (box.fill || 'transparent'),
                // Softer, staggered tiny round-dot pattern (two layers, slightly offset)
                backgroundImage: isShaded
                  ? `
                    radial-gradient(circle, ${toRgba(patternColor, 0.3)} 0.5px, transparent 2px),
                    radial-gradient(circle, ${toRgba(patternColor, 0.3)} 0.5px, transparent 2px)
                  `
                  : 'none',
                backgroundSize: isShaded ? '10px 10px, 10px 10px' : 'auto',
                backgroundPosition: isShaded ? '0 0, 5px 5px' : '0 0',
                  cursor: 'move'
              }}
              onMouseDown={(e) => {
                if (!canvasRef.current) return;
                  // Don't start drag if clicking on a resize handle
                  if ((e.target as HTMLElement).closest('.box-resize-handle')) return;
                e.stopPropagation();
                if (e.button !== 0) return;
                setSelectedBoxId(box.id);
                setShowBoxTools(true);
                  // Enable dragging immediately when clicking on a box
                  const current = getCanvasCoordinates(e.clientX, e.clientY);
                  setDraggingBoxId(box.id);
                  setBoxDragOffset({
                    dx: current.x - box.x,
                    dy: current.y - box.y
                  });
                }}
              />
              {/* Resize handles - only show when box is selected */}
              {isSelected && handles.map(handle => (
                <div
                  key={handle.pos}
                  className="box-resize-handle absolute bg-blue-500 border-2 border-white rounded-full z-10"
                  style={{
                    left: box.x + handle.x,
                    top: box.y + handle.y,
                    width: handleSize,
                    height: handleSize,
                    cursor: handle.cursor,
                    pointerEvents: 'all'
                  }}
                  onMouseDown={(e) => {
                    if (!canvasRef.current) return;
                    e.stopPropagation();
                    e.preventDefault();
                    if (e.button !== 0) return;
                    // Ensure box stays selected when starting resize
                    setSelectedBoxId(box.id);
                    setShowBoxTools(true);
                    const current = getCanvasCoordinates(e.clientX, e.clientY);
                    setResizingBoxId(box.id);
                    setResizeHandle(handle.pos);
                    setResizeStartState({
                      boxX: box.x,
                      boxY: box.y,
                      boxWidth: box.width,
                      boxHeight: box.height,
                      mouseX: current.x,
                      mouseY: current.y
                    });
                  }}
            />
              ))}
            </div>
          );
        })}
        {boxPreview && (
          <div
            className="absolute rounded-xl pointer-events-none"
            style={{
              left: boxPreview.x,
              top: boxPreview.y,
              width: boxPreview.width,
              height: boxPreview.height,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: boxPreview.stroke,
              backgroundColor: 'transparent',
              backgroundImage: boxPreview.fill
                ? `
                  radial-gradient(circle, ${toRgba(boxPreview.stroke, 0.3)} 0.5px, transparent 2px),
                  radial-gradient(circle, ${toRgba(boxPreview.stroke, 0.3)} 0.5px, transparent 2px)
                `
                : 'none',
              backgroundSize: boxPreview.fill ? '10px 10px, 10px 10px' : 'auto',
              backgroundPosition: boxPreview.fill ? '0 0, 5px 5px' : '0 0',
              opacity: 0.8
            }}
          />
        )}

        {/* Canvas labels */}
        {labels.filter((label) => isVisibleOnCanvas(designForRender, 'label', label)).map(label => {
          const isSelected = selectedLabelId === label.id;
          return (
            <div
              key={label.id}
              className={`absolute canvas-label ${isSelected && !label.isEditing ? 'shadow-[0_0_0_2px_rgba(34,197,94,0.7)] rounded-md' : ''}`}
              data-label-id={label.id}
              style={{
                left: label.x,
                top: label.y,
                padding: label.isEditing ? '4px 8px' : '4px 8px',
                cursor: (draggingLabelId === label.id || isSelected) ? 'move' : 'default',
                zIndex: label.isEditing ? 100 : 50,
                backgroundColor: label.isEditing ? 'rgba(15, 23, 42, 0.95)' : 'rgba(15, 23, 42, 0.85)',
                border: label.isEditing ? '2px solid #10b981' : '1px solid rgba(34, 197, 94, 0.5)',
                borderRadius: '4px',
                backdropFilter: 'blur(4px)',
                userSelect: label.isEditing ? 'text' : 'none',
              }}
              onMouseDown={(e) => {
                if (!canvasRef.current) return;
                // If clicking a resize handle, let that handler take over
                if ((e.target as HTMLElement).closest('.label-resize-handle')) return;
                // If clicking the input field, don't start drag
                if ((e.target as HTMLElement).tagName === 'INPUT') return;
                e.preventDefault();
                e.stopPropagation();
                if (e.button !== 0) return;
                
                // Store click position to detect if it was a click vs drag
                labelClickStartPos.current = { x: e.clientX, y: e.clientY };
                
                // Mark that we just clicked a label to prevent deselection
                justClickedLabelRef.current = true;
                setTimeout(() => {
                  justClickedLabelRef.current = false;
                }, 150);
                
                setSelectedLabelId(label.id);
                setShowLabelTools(true);
                // Enable move mode immediately on click (like boxes)
                if (!label.isEditing) {
                  const current = getCanvasCoordinates(e.clientX, e.clientY);
                  setDraggingLabelId(label.id);
                  setLabelDragOffset({
                    dx: current.x - label.x,
                    dy: current.y - label.y
                  });
                }
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (label.isEditing) return;
                // Enable editing mode on double-click and store original text
                setLabels(prev =>
                  prev.map(l =>
                    l.id === label.id ? { ...l, isEditing: true, originalText: l.text } : l
                  )
                );
                setSelectedLabelId(label.id);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Show menu on right-click
                setSelectedLabelId(label.id);
                setShowLabelTools(true);
              }}
            >
              {label.isEditing ? (
                <input
                  key={`label-input-${label.id}`}
                  ref={(input) => {
                    if (input) {
                      // Use a flag to only initialize once per edit session
                      const initKey = `label-init-${label.id}`;
                      if (!(window as any)[initKey]) {
                        (window as any)[initKey] = true;
                        // Small delay to ensure DOM is ready
                        setTimeout(() => {
                      input.focus();
                          // Only select all if it's the default "Label" text
                          if (label.text === 'Label' || !label.text) {
                      input.select();
                          } else {
                            // Place cursor at end
                            const len = input.value.length;
                            input.setSelectionRange(len, len);
                          }
                          // Initial resize
                          const resizeInput = () => {
                            const minWidth = 60;
                            const maxWidth = 400;
                            const tempSpan = document.createElement('span');
                            tempSpan.style.visibility = 'hidden';
                            tempSpan.style.position = 'absolute';
                            tempSpan.style.fontFamily = label.fontFamily;
                            tempSpan.style.fontSize = `${label.fontSize}px`;
                            tempSpan.style.padding = '0';
                            tempSpan.style.whiteSpace = 'pre';
                            tempSpan.textContent = input.value || input.placeholder || 'Label';
                            document.body.appendChild(tempSpan);
                            const width = Math.max(minWidth, Math.min(maxWidth, tempSpan.offsetWidth + 20));
                            input.style.width = `${width}px`;
                            document.body.removeChild(tempSpan);
                          };
                          resizeInput();
                        }, 0);
                      }
                    }
                  }}
                  className="bg-transparent border-none outline-none text-white relative z-50 placeholder:text-gray-400"
                  style={{
                    fontFamily: label.fontFamily,
                    fontSize: label.fontSize,
                    minWidth: '60px',
                    width: '60px',
                    color: '#ffffff'
                  }}
                  placeholder="Type label text..."
                  value={label.text || ''}
                  onClick={e => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    const input = e.target as HTMLInputElement;
                    const value = input.value;
                    
                    // Update state with the new value immediately
                    setLabels(prev =>
                      prev.map(l => {
                        if (l.id === label.id) {
                          return { ...l, text: value };
                        }
                        return l;
                      })
                    );
                    
                    // Auto-resize on change (defer to avoid blocking input)
                    setTimeout(() => {
                      if (!input) return;
                      const minWidth = 60;
                      const maxWidth = 400;
                      const tempSpan = document.createElement('span');
                      tempSpan.style.visibility = 'hidden';
                      tempSpan.style.position = 'absolute';
                      tempSpan.style.fontFamily = label.fontFamily;
                      tempSpan.style.fontSize = `${label.fontSize}px`;
                      tempSpan.style.padding = '0';
                      tempSpan.style.whiteSpace = 'pre';
                      tempSpan.textContent = value || input.placeholder || 'Label';
                      document.body.appendChild(tempSpan);
                      const width = Math.max(minWidth, Math.min(maxWidth, tempSpan.offsetWidth + 20));
                      input.style.width = `${width}px`;
                      document.body.removeChild(tempSpan);
                    }, 0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      // Clear init flag when saving
                      const initKey = `label-init-${label.id}`;
                      delete (window as any)[initKey];
                      // Save on Enter
                      setLabels(prev =>
                        prev.map(l =>
                          l.id === label.id
                            ? { ...l, isEditing: false, text: l.text.trim() || 'Label', originalText: undefined }
                            : l
                        )
                      );
                      setShowLabelTools(false);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();
                      // Clear init flag when canceling
                      const initKey = `label-init-${label.id}`;
                      delete (window as any)[initKey];
                      // Cancel on Escape - revert to original text
                      setLabels(prev =>
                        prev.map(l =>
                          l.id === label.id
                            ? { ...l, isEditing: false, text: l.originalText || l.text, originalText: undefined }
                            : l
                        )
                      );
                      setShowLabelTools(false);
                    }
                  }}
                  onBlur={() => {
                    // Clear init flag when blurring
                    const initKey = `label-init-${label.id}`;
                    delete (window as any)[initKey];
                    // Cancel on blur (clicking outside) - revert to original text
                    setLabels(prev =>
                      prev.map(l => {
                        if (l.id !== label.id) return l;
                        const revertedText = l.originalText !== undefined 
                          ? l.originalText 
                          : (l.text.trim() || 'Label');
                        return { ...l, isEditing: false, text: revertedText, originalText: undefined };
                      })
                    );
                    setShowLabelTools(false);
                  }}
                />
              ) : (
                <span
                  className="text-white select-none whitespace-nowrap"
                  style={{
                    fontFamily: label.fontFamily,
                    fontSize: label.fontSize,
                    color: '#ffffff',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {label.text || 'Label'}
                </span>
              )}
              {/* Corner resize handles (4 corners for proportional resize) */}
              {!label.isEditing && isSelected && (
                <>
                  {/* Top-left */}
                <div
                    className="label-resize-handle absolute -top-1 -left-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white cursor-nw-resize z-50"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedLabelId(label.id);
                    setResizingLabelId(label.id);
                      setLabelResizeHandle('nw');
                      // Get canvas coordinates for the mouse position at start
                      if (!canvasRef.current) return;
                      const startRect = canvasRef.current.getBoundingClientRect();
                      const startCanvasX = e.clientX - startRect.left - canvasPanX;
                      const startCanvasY = e.clientY - startRect.top - canvasPanY;
                      
                      setLabelResizeStartState({
                        labelX: label.x,
                        labelY: label.y,
                        labelFontSize: label.fontSize,
                        mouseX: startCanvasX,
                        mouseY: startCanvasY
                      });
                    }}
                  />
                  {/* Top-right */}
                  <div
                    className="label-resize-handle absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white cursor-ne-resize z-50"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedLabelId(label.id);
                      setResizingLabelId(label.id);
                      setLabelResizeHandle('ne');
                      // Get canvas coordinates for the mouse position at start
                      if (!canvasRef.current) return;
                      const startRect = canvasRef.current.getBoundingClientRect();
                      const startCanvasX = e.clientX - startRect.left - canvasPanX;
                      const startCanvasY = e.clientY - startRect.top - canvasPanY;
                      
                      setLabelResizeStartState({
                        labelX: label.x,
                        labelY: label.y,
                        labelFontSize: label.fontSize,
                        mouseX: startCanvasX,
                        mouseY: startCanvasY
                      });
                    }}
                  />
                  {/* Bottom-right */}
                  <div
                    className="label-resize-handle absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white cursor-se-resize z-50"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedLabelId(label.id);
                      setResizingLabelId(label.id);
                      setLabelResizeHandle('se');
                      // Get canvas coordinates for the mouse position at start
                      if (!canvasRef.current) return;
                      const startRect = canvasRef.current.getBoundingClientRect();
                      const startCanvasX = e.clientX - startRect.left - canvasPanX;
                      const startCanvasY = e.clientY - startRect.top - canvasPanY;
                      
                      setLabelResizeStartState({
                        labelX: label.x,
                        labelY: label.y,
                        labelFontSize: label.fontSize,
                        mouseX: startCanvasX,
                        mouseY: startCanvasY
                      });
                    }}
                  />
                  {/* Bottom-left */}
                  <div
                    className="label-resize-handle absolute -bottom-1 -left-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white cursor-sw-resize z-50"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedLabelId(label.id);
                      setResizingLabelId(label.id);
                      setLabelResizeHandle('sw');
                      // Get canvas coordinates for the mouse position at start
                      if (!canvasRef.current) return;
                      const startRect = canvasRef.current.getBoundingClientRect();
                      const startCanvasX = e.clientX - startRect.left - canvasPanX;
                      const startCanvasY = e.clientY - startRect.top - canvasPanY;
                      
                      setLabelResizeStartState({
                        labelX: label.x,
                        labelY: label.y,
                        labelFontSize: label.fontSize,
                        mouseX: startCanvasX,
                        mouseY: startCanvasY
                      });
                  }}
                />
                </>
              )}
            </div>
          );
        })}

        {/* Label tools menu */}
        {selectedLabelId && showLabelTools && (() => {
          const label = labels.find(l => l.id === selectedLabelId);
          if (!label || label.isEditing) return null; // Hide menu when editing
          
          const labelWidth = Math.max(60, measureLabelWidth(label.text || 'Label', label.fontSize, label.fontFamily));
          
          // Account for padding (4px left + 4px right = 8px total)
          const labelTotalWidth = labelWidth + 8;
          
          return (
            <div
              className="absolute z-50 label-tools-menu"
              style={{
                left: label.x + labelTotalWidth + 16,
                top: label.y - 4
              }}
            >
              <div className="bg-slate-900/95 text-white text-[11px] rounded-md shadow-lg border border-slate-700 px-2 py-1 space-y-1.5">
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setLabels(prev => prev.filter(l => l.id !== label.id));
                      setSelectedLabelId(null);
                      setShowLabelTools(false);
                    }}
                    className="px-2.5 py-1 rounded-md border text-xs bg-red-600 border-red-500 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
                <div className="pt-1 border-t border-slate-700">
                  <div className="flex gap-1">
                    {[
                      {
                        label: 'System',
                        value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                      },
                      {
                        label: 'Mono',
                        value: '"Fira Code", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                      },
                      {
                        label: 'Display',
                        value: '"Poppins", "Montserrat", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                      }
                    ].map((opt) => (
                      <button
                        key={opt.label}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLabels(prev =>
                            prev.map(l =>
                              l.id === label.id ? { ...l, fontFamily: opt.value } : l
                            )
                          );
                          setLabelFont(opt.value);
                        }}
                        className={`px-2.5 py-1 rounded-md border text-xs ${
                          label.fontFamily === opt.value
                            ? 'bg-emerald-600 border-emerald-500 text-white'
                            : 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700'
                        }`}
                        style={{
                          fontFamily: opt.value
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pt-1 border-t border-slate-700">
                  <div className="flex gap-1 items-center">
                    {[12, 14, 16, 18, 20].map(size => (
                      <button
                        key={size}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLabels(prev =>
                            prev.map(l =>
                              l.id === label.id ? { ...l, fontSize: size } : l
                            )
                          );
                          setLabelFontSize(size);
                        }}
                        className={`px-2.5 py-1 rounded-md border text-xs ${
                          Math.round(label.fontSize) === size
                            ? 'bg-emerald-600 border-emerald-500 text-white'
                            : 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Box tools menu */}
        {selectedBoxId && showBoxTools && (() => {
          const box = boxes.find(b => b.id === selectedBoxId);
          if (!box) return null;
          return (
            <div
              className="absolute z-50 box-tools-menu"
              style={{
                left: box.x + box.width + 8,
                top: box.y - 4
              }}
            >
              <div className="bg-slate-900/95 text-white text-[11px] rounded-md shadow-lg border border-slate-700 px-2 py-1 space-y-1.5">
                <div className="flex gap-1 items-center justify-between">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setBoxes(prev => prev.filter(b => b.id !== box.id));
                      setSelectedBoxId(null);
                      setShowBoxTools(false);
                    }}
                    className="px-2.5 py-1 rounded-md border text-xs bg-red-600 border-red-500 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setBoxes(prev =>
                        prev.map(b =>
                          b.id === box.id
                            ? {
                                ...b,
                                stroke: b.stroke,
                                // Toggle shaded: if already shaded, remove it; otherwise enable it
                                patternColor: b.patternColor ? undefined : b.stroke
                              }
                            : b
                        )
                      );
                    }}
                    className={`px-2.5 py-1 rounded-md border text-xs transition-colors ${
                      box.patternColor
                        ? 'bg-blue-600 border-blue-500 hover:bg-blue-700'
                        : 'bg-slate-800 border-slate-600 hover:bg-slate-700'
                    }`}
                    style={{ borderColor: box.patternColor ? '#3b82f6' : '#475569' }}
                  >
                    Shaded
                  </button>
                </div>
                <div className="pt-1 border-t border-slate-700">
                  <div className="flex flex-col gap-1 mb-1">
                    {/* First row */}
                    <div className="flex gap-1">
                    {[
                      { stroke: '#60a5fa' }, // pastel blue
                      { stroke: '#fbbf24' }, // pastel amber
                        { stroke: '#c4b5fd' }, // pastel purple
                        { stroke: '#f87171' }, // pastel red
                        { stroke: '#34d399' }, // pastel emerald
                        { stroke: '#a78bfa' }, // pastel violet
                        { stroke: '#fb7185' }, // pastel rose
                        { stroke: '#38bdf8' }, // pastel sky
                        { stroke: '#fde047' }, // pastel yellow
                        { stroke: '#818cf8' }, // pastel indigo
                        { stroke: '#fb923c' }  // pastel orange
                    ].map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBoxes(prev =>
                            prev.map(b =>
                              b.id === box.id
                              ? {
                                  ...b,
                                  stroke: opt.stroke,
                                  // keep shaded state; pattern colour should follow stroke
                                  patternColor: b.patternColor ? opt.stroke : b.patternColor
                                }
                                : b
                            )
                          );
                        }}
                          className="w-5 h-5 rounded-md border-2 transition-colors hover:opacity-80"
                          style={{ backgroundColor: opt.stroke, borderColor: opt.stroke }}
                        title="Change outline colour"
                      />
                    ))}
                  </div>
                    {/* Second row */}
                    <div className="flex gap-1">
                      {[
                        { stroke: '#a5b4fc' }, // pastel lavender
                        { stroke: '#86efac' }, // pastel mint
                        { stroke: '#fda4af' }, // pastel pink
                        { stroke: '#93c5fd' }, // pastel light blue
                        { stroke: '#c084fc' }, // pastel light purple
                        { stroke: '#fbbf24' }, // pastel gold
                        { stroke: '#fca5a5' }, // pastel coral
                        { stroke: '#94a3b8' }, // pastel gray
                        { stroke: '#fcd34d' }, // pastel lemon
                        { stroke: '#a7f3d0' }, // pastel aqua
                        { stroke: '#ddd6fe' }  // pastel lilac
                      ].map((opt, idx) => (
                    <button
                          key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        setBoxes(prev =>
                          prev.map(b =>
                            b.id === box.id
                              ? {
                                  ...b,
                                    stroke: opt.stroke,
                                    // keep shaded state; pattern colour should follow stroke
                                    patternColor: b.patternColor ? opt.stroke : b.patternColor
                                }
                              : b
                          )
                        );
                      }}
                          className="w-5 h-5 rounded-md border-2 transition-colors hover:opacity-80"
                          style={{ backgroundColor: opt.stroke, borderColor: opt.stroke }}
                          title="Change outline colour"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Grid overlay removed per request: render pure white background */}

      {roomNamePrompt && (
        <div className="absolute inset-0 z-[70] flex items-center justify-center pointer-events-none" data-floorplan-ui>
          <div className="pointer-events-auto bg-slate-900/95 border border-slate-600 rounded-xl shadow-2xl p-4 w-[300px] text-white">
            <div className="text-sm font-semibold mb-1">
              {roomNamePrompt.mode === 'rename' ? 'Rename room' : 'Name this room'}
            </div>
            <p className="text-xs text-slate-400 mb-3">
              {roomNamePrompt.mode === 'rename'
                ? 'Enter a new name for this room.'
                : 'A closed wall loop was found. Give it a label.'}
            </p>
            <input
              autoFocus
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmRoomName();
                if (e.key === 'Escape') cancelRoomName();
              }}
              placeholder="e.g. Server Room"
              className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:border-blue-500"
            />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={cancelRoomName} className="px-3 py-1.5 text-xs rounded-lg text-slate-300 hover:bg-slate-800">
                Cancel
              </button>
              <button type="button" onClick={confirmRoomName} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-500 font-medium">
                {roomNamePrompt.mode === 'rename' ? 'Save' : 'Add room'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fusion-style design browser (all canvas objects) */}
      {designForRender && (
        <DesignBrowser
          isDark={isDarkTheme}
          boxes={boxes}
          labels={labels}
          selectedBoxId={selectedBoxId}
          selectedLabelId={selectedLabelId}
          onSelectBox={(id) => {
            setSelectedBoxId(id);
            setShowBoxTools(!!id);
          }}
          onSelectLabel={(id) => {
            setSelectedLabelId(id);
            setShowLabelTools(!!id);
          }}
          onEditLabel={(id) => {
            setSelectedLabelId(id);
            setShowLabelTools(true);
            setLabels((prev) =>
              prev.map((l) =>
                l.id === id
                  ? { ...l, isEditing: true, originalText: l.text }
                  : { ...l, isEditing: false, originalText: undefined }
              )
            );
          }}
          onToggleBoxHidden={(id, hidden) => {
            setBoxes((prev) => prev.map((b) => (b.id === id ? { ...b, hidden } : b)));
          }}
          onToggleLabelHidden={(id, hidden) => {
            setLabels((prev) => prev.map((l) => (l.id === id ? { ...l, hidden } : l)));
          }}
          onRenameRoom={startRenameRoom}
        />
      )}

      <LogicSimulationHost />
      <LogicStatusDrawer isDark={isDarkTheme} />

      {/* Canvas Toolbar - Top Left */}
      {showCanvasToolbar && (
        <div className="absolute top-4 left-4 z-50 flex items-start gap-1 pointer-events-none" data-canvas-chrome="true">
          {/* Toolbar buttons - horizontal bar */}
          <div
            className="bg-slate-800/90 backdrop-blur-sm rounded-lg shadow-lg p-0 flex flex-row gap-0 pointer-events-auto overflow-visible h-8"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative group">
              <button
                onClick={() => {
                  setSelectedRack(null);
                  setRackBuilderOpen(true);
                }}
                className="px-2.5 bg-slate-700 hover:bg-slate-600 transition-colors flex items-center justify-center h-8 rounded-l-lg"
              >
                <Server className="text-blue-400" size={16} />
              </button>
              {/* Tooltip aligned to window edge to prevent clipping */}
              <div className="absolute top-full left-0 mt-1 hidden group-hover:block pointer-events-none">
                <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700 ml-1">
                  Rack Builder
                  <div className="absolute left-3 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                </div>
              </div>
            </div>
            <div className="relative group">
              <button
                onClick={() => setAddEquipmentOpen(true)}
                className="px-2.5 bg-slate-700 hover:bg-slate-600 transition-colors flex items-center justify-center h-8"
              >
                <Package className="text-green-400" size={16} />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none">
                <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                  Add Equipment
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                </div>
              </div>
            </div>
            <div className="relative group" data-cable-run-toolbar="true">
              <button
                onClick={() => {
                  if (isCableRunMode) {
                    setCableRunMode(false);
                    setCableRoutingPopoverOpen(false);
                    return;
                  }
                  setCableRoutingPopoverOpen((open) => !open);
                }}
                className={`px-2.5 transition-colors flex items-center justify-center h-8 ${
                  isCableRunMode || cableRoutingPopoverOpen
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                <Cable className={isCableRunMode || cableRoutingPopoverOpen ? 'text-white' : 'text-purple-400'} size={16} />
              </button>
              {cableRoutingPopoverOpen ? (
                <CableRunRoutingPopover
                  onSelect={(routing) => {
                    setCableRunRouting(routing);
                    setCableRoutingPopoverOpen(false);
                    setCableRunMode(true);
                    setIsDrawingBox(false);
                    setIsLabelPlacementMode(false);
                    setCanvasMode('place');
                  }}
                />
              ) : (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none">
                  <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                    Cable Run
                    <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative group">
              <button
                onClick={() => {
                  const next = logicMode === 'logic' ? 'plan' : 'logic';
                  useProjectStore.getState().patchCurrentDesign({ logicMode: next });
                  const logic = useLogicStore.getState();
                  if (next === 'logic') logic.setDrawerOpen(true);
                  else logic.setDrawerOpen(false);
                }}
                className={`px-2.5 transition-colors flex items-center justify-center h-8 ${
                  logicMode === 'logic' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'bg-slate-700 hover:bg-slate-600'
                }`}
                title={logicMode === 'logic' ? 'Logic mode on' : 'Plan mode — click for Logic'}
              >
                <Cpu className={logicMode === 'logic' ? 'text-white' : 'text-cyan-300'} size={16} />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none">
                <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                  {logicMode === 'logic' ? 'Logic' : 'Plan'}
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                </div>
              </div>
            </div>
            {logicMode === 'logic' && (
            <div className="relative group">
              <button
                onClick={() => useLogicStore.getState().setDrawerOpen(!logicDrawerOpen)}
                className={`px-2.5 transition-colors flex items-center justify-center h-8 ${
                  logicDrawerOpen ? 'bg-slate-600 hover:bg-slate-500' : 'bg-slate-700 hover:bg-slate-600'
                }`}
                title={logicDrawerOpen ? 'Hide Logic status' : 'Show Logic status'}
              >
                {logicHealthOk(logicResult, logicHideIdle) ? (
                  <CheckCircle2 className="text-emerald-400" size={16} />
                ) : (
                  <AlertTriangle className="text-amber-400" size={16} />
                )}
              </button>
            </div>
            )}
            <div className="relative group">
              <button
                onClick={(e) => {
                  const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                  setNodeContextMenu({ isOpen: true, position: { x: rect.right + 10, y: rect.top } });
                }}
                className="px-2.5 bg-slate-700 hover:bg-slate-600 transition-colors flex items-center justify-center h-8"
              >
                <Circle className="text-cyan-400" size={16} />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:block pointer-events-none">
                <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                  Add Node
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                </div>
              </div>
            </div>
            <div className="relative group">
              <button
                ref={boxPaletteButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDrawingBox) {
                    setIsDrawingBox(false);
                    setSelectedBoxStyle(null);
                    setBoxStart(null);
                    setBoxPreview(null);
                    setShowBoxPalette(false);
                    return;
                  }
                  if (showBoxPalette) {
                    setShowBoxPalette(false);
                  } else {
                    setShowBoxPalette(true);
                    setSelectedBoxStyle({ stroke: lastSelectedBoxColor });
                  }
                }}
                className={`px-2.5 transition-colors flex items-center justify-center h-8 ${
                  isDrawingBox
                    ? 'bg-sky-600 hover:bg-sky-700 text-white'
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                <SquareDashed className={isDrawingBox ? 'text-white' : 'text-sky-300'} size={16} />
              </button>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 ${showBoxPalette ? 'hidden' : 'hidden group-hover:block'} pointer-events-none`}>
                <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                  Add Box
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                </div>
              </div>
              {showBoxPalette && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 pointer-events-auto" ref={boxPaletteRef}>
                  <div className="bg-slate-900/95 text-white text-[11px] rounded-md shadow-lg border border-slate-700 px-2 py-1 space-y-1.5">
                    <div className="flex flex-col gap-1 mb-1">
                      {/* First row */}
                      <div className="flex gap-1">
                      {[
                        { stroke: '#60a5fa' }, // pastel blue
                        { stroke: '#fbbf24' }, // pastel amber
                          { stroke: '#c4b5fd' }, // pastel purple
                          { stroke: '#f87171' }, // pastel red
                          { stroke: '#34d399' }, // pastel emerald
                          { stroke: '#a78bfa' }, // pastel violet
                          { stroke: '#fb7185' }, // pastel rose
                          { stroke: '#38bdf8' }, // pastel sky
                          { stroke: '#fde047' }, // pastel yellow
                          { stroke: '#818cf8' }, // pastel indigo
                          { stroke: '#fb923c' }  // pastel orange
                      ].map((opt, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                              // Preserve shaded state when selecting a color
                              const isShaded = selectedBoxStyle?.fill !== undefined;
                              setSelectedBoxStyle({ 
                                stroke: opt.stroke,
                                fill: isShaded ? opt.stroke : undefined
                              });
                              setLastSelectedBoxColor(opt.stroke); // Track last selected color
                            setIsDrawingBox(true);
                            setShowBoxPalette(false);
                          }}
                            className={`w-5 h-5 rounded-md border-2 transition-colors hover:opacity-80 ${
                              (selectedBoxStyle?.stroke === opt.stroke || (!selectedBoxStyle && lastSelectedBoxColor === opt.stroke))
                                ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-900'
                                : ''
                            }`}
                            style={{ 
                              backgroundColor: opt.stroke, 
                              borderColor: opt.stroke 
                            }}
                          title="Draw outline box"
                        />
                      ))}
                    </div>
                      {/* Second row */}
                      <div className="flex gap-1">
                        {[
                          { stroke: '#a5b4fc' }, // pastel lavender
                          { stroke: '#86efac' }, // pastel mint
                          { stroke: '#fda4af' }, // pastel pink
                          { stroke: '#93c5fd' }, // pastel light blue
                          { stroke: '#c084fc' }, // pastel light purple
                          { stroke: '#fbbf24' }, // pastel gold
                          { stroke: '#fca5a5' }, // pastel coral
                          { stroke: '#94a3b8' }, // pastel gray
                          { stroke: '#fcd34d' }, // pastel lemon
                          { stroke: '#a7f3d0' }, // pastel aqua
                          { stroke: '#ddd6fe' }  // pastel lilac
                        ].map((opt, idx) => (
                      <button
                            key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                              // Preserve shaded state when selecting a color
                              const isShaded = selectedBoxStyle?.fill !== undefined;
                          setSelectedBoxStyle({
                                stroke: opt.stroke,
                                fill: isShaded ? opt.stroke : undefined
                          });
                              setLastSelectedBoxColor(opt.stroke); // Track last selected color
                          setIsDrawingBox(true);
                          setShowBoxPalette(false);
                        }}
                            className={`w-5 h-5 rounded-md border-2 transition-colors hover:opacity-80 ${
                              (selectedBoxStyle?.stroke === opt.stroke || (!selectedBoxStyle && lastSelectedBoxColor === opt.stroke))
                                ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-900'
                                : ''
                            }`}
                            style={{ 
                              backgroundColor: opt.stroke, 
                              borderColor: opt.stroke 
                            }}
                            title="Draw outline box"
                          />
                        ))}
                      </div>
                    </div>
                    <div className="pt-1 border-t border-slate-700">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Toggle shaded state instead of always enabling it
                          const isCurrentlyShaded = selectedBoxStyle?.fill !== undefined;
                          if (isCurrentlyShaded) {
                            // Disable shaded - keep current stroke color
                            setSelectedBoxStyle({
                              stroke: selectedBoxStyle.stroke || '#60a5fa'
                            });
                          } else {
                            // Enable shaded - use current stroke color or default
                            setSelectedBoxStyle({
                              stroke: selectedBoxStyle?.stroke || '#60a5fa',
                              fill: selectedBoxStyle?.stroke || '#60a5fa'
                            });
                          }
                          // Don't close menu or start drawing - let user select color first
                        }}
                        className={`w-full px-2.5 py-1 rounded-md border text-xs transition-colors ${
                          selectedBoxStyle?.fill
                            ? 'bg-blue-600 border-blue-500 hover:bg-blue-700'
                            : 'bg-slate-800 border-slate-600 hover:bg-slate-700'
                        }`}
                        style={{ borderColor: selectedBoxStyle?.fill ? '#3b82f6' : '#475569' }}
                      >
                        Shaded
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="relative group">
              <button
                ref={labelPaletteButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isLabelPlacementMode) {
                    setIsLabelPlacementMode(false);
                    setShowLabelPalette(false);
                    return;
                  }
                  if (showLabelPalette) {
                    setShowLabelPalette(false);
                  } else {
                    setShowLabelPalette(true);
                  }
                }}
                className={`px-2.5 transition-colors flex items-center justify-center h-8 ${
                  isLabelPlacementMode
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                <Type className={isLabelPlacementMode ? 'text-white' : 'text-emerald-300'} size={16} />
              </button>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-1 ${showLabelPalette ? 'hidden' : 'hidden group-hover:block'} pointer-events-none`}>
                <div className="relative bg-slate-800/95 text-white text-[11px] px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap border border-slate-700">
                  Add Label
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-slate-800/95 border-t border-l border-slate-700 rotate-45"></div>
                </div>
              </div>
              {showLabelPalette && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 pointer-events-auto" ref={labelPaletteRef}>
                  <div className="bg-slate-900/95 text-white text-[11px] rounded-md shadow-lg border border-slate-700 px-2 py-1 space-y-1.5 min-w-[200px]">
                      <div className="flex gap-1 flex-wrap">
                        {[
                          {
                            label: 'System',
                            value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                          },
                          {
                            label: 'Mono',
                            value: '"Fira Code", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                          },
                          {
                            label: 'Display',
                            value: '"Poppins", "Montserrat", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                          }
                        ].map(opt => (
                          <button
                            key={opt.label}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLabelFont(opt.value);
                            }}
                          className={`px-2.5 py-1 rounded-md border text-xs ${
                              labelFont === opt.value
                                ? 'bg-emerald-600 border-emerald-500 text-white'
                                : 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700'
                            }`}
                          style={{
                            fontFamily: opt.value
                          }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-1 items-center">
                        {[12, 14, 16, 18, 20].map(size => (
                          <button
                            key={size}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLabelFontSize(size);
                            }}
                          className={`px-2.5 py-1 rounded-md border text-xs ${
                              labelFontSize === size
                                ? 'bg-emerald-600 border-emerald-500 text-white'
                                : 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsLabelPlacementMode(true);
                        setShowLabelPalette(false);
                      }}
                      className="w-full mt-1 px-2 py-1 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-medium"
                    >
                      Click on canvas to add label
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Floor plan tools â€” same icon style, extends the bar */}
            <div className="flex flex-row h-8" data-floorplan-ui>
              <FloorPlanToolbar />
            </div>
          </div>
        </div>
      )}

      {/* Design Info Overlay - Top Right */}
      {currentProject && designForRender && showDesignInfoOverlay && (
        <div className={`absolute top-4 right-4 backdrop-blur-sm rounded-lg p-4 text-white text-xs shadow-lg z-50 pointer-events-auto max-w-[280px] ${
          isDarkTheme ? 'bg-[#20242b]/95 border border-[#343a43]' : 'bg-slate-800/90'
        }`}>
          <h3 className="text-sm font-medium text-gray-300 uppercase tracking-wider mb-3">Design Info</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-400">Name:</span>
              <span className="ml-2 font-medium">{currentDesign?.name || currentProject.name}</span>
            </div>
            <div>
              <span className="text-gray-400">Customer:</span>
              <span className="ml-2 font-medium">{currentProject.customer.name}</span>
            </div>
            <div>
              <span className="text-gray-400">Address:</span>
              <span className="ml-2 font-medium">{currentProject.customer.address}</span>
            </div>
            <div className={`pt-2 border-t ${isDarkTheme ? 'border-[#343a43]' : 'border-slate-700'}`}>
              <span className="text-gray-400">Equipment:</span>
              <span className="ml-2 font-medium">
                {designForRender.equipment.filter(eq => !eq.isExisting).length || 0} new, {designForRender.equipment.filter(eq => eq.isExisting).length || 0} existing
              </span>
            </div>
            <div>
              <span className="text-gray-400">Racks:</span>
              <span className="ml-2 font-medium">
                {designForRender.racks.filter(rack => !rack.isExisting).length || 0} new, {designForRender.racks.filter(rack => rack.isExisting).length || 0} existing
              </span>
            </div>
            <div>
              <span className="text-gray-400">Cables:</span>
              <span className="ml-2 font-medium">
                {cablesForRender.filter(cable => !cable.isExisting).length || 0} new, {cablesForRender.filter(cable => cable.isExisting).length || 0} existing
              </span>
            </div>
            <div>
              <span className="text-gray-400">Est. Cost:</span>
              <span className="ml-2 font-medium text-green-400">
                {formatCurrencyNoDecimals(
                  designForRender.equipment
                    .filter(eq => !eq.isExisting)
                    .reduce((total, eq) => total + (eq.equipment.price || 0), 0) || 0,
                  currency
                )}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Existing:</span>
              <span className={`ml-2 font-medium ${isDarkTheme ? 'text-gray-300' : 'text-blue-400'}`}>
                {formatCurrencyNoDecimals(
                  designForRender.equipment
                    .filter(eq => eq.isExisting)
                    .reduce((total, eq) => total + (eq.equipment.price || 0), 0) || 0,
                  currency
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Zoom Level Indicator */}
      <div 
        className={`absolute bottom-4 right-4 z-50 pointer-events-none transition-opacity duration-300 ${
          useProjectStore.getState().showMinimap ? 'mb-36' : ''
        }`}
        style={{
          opacity: showZoomIndicator ? 1 : 0,
          transition: 'opacity 0.3s ease-out'
        }}
      >
        <div className="bg-slate-800/90 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-lg text-white text-xs font-medium">
          {Math.round(canvasZoom * 100)}%
        </div>
      </div>

      {/* Minimap - conditional */}
      {useProjectStore.getState().showMinimap && (
      <div className="absolute bottom-4 right-4 z-40 group" data-canvas-chrome="true">
        <div
          className="bg-slate-800/90 backdrop-blur-sm p-2 rounded-lg shadow-lg pointer-events-auto transition-all duration-200 group-hover:p-3"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold text-white">Minimap</div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleViewObjects}
                className="p-1 hover:bg-slate-700 rounded transition-colors"
                title="View Objects"
              >
                <Maximize2 size={12} className="text-slate-300 hover:text-white" />
              </button>
              <button
                onClick={handleRecenter}
                className="p-1 hover:bg-slate-700 rounded transition-colors"
                title="Recenter Objects"
              >
                <Move size={12} className="text-slate-300 hover:text-white" />
              </button>
            </div>
          </div>
          <div className="relative w-40 h-28 group-hover:w-64 group-hover:h-40 transition-all duration-200 bg-slate-700 rounded border border-slate-600 overflow-hidden">
          {/* Floorplan representation */}
          {currentProject.floorplan && (
            <div 
              className="absolute bg-blue-500/50 border border-blue-400"
              style={{
                left: '10%',
                top: '10%',
                width: '80%',
                height: '80%'
              }}
              title="Floorplan"
            />
          )}
          
          {/* Equipment representation */}
          {designForRender.equipment.map((equipment, index) => (
            <div
              key={equipment.equipment.id}
              className="absolute bg-green-400/80 w-1.5 h-1.5 rounded-full"
              style={{
                left: `${10 + (equipment.position.x / 2000) * 80}%`,
                top: `${10 + (equipment.position.y / 2000) * 80}%`
              }}
              title={equipment.equipment.name}
            />
          ))}
          
          {/* Racks representation */}
          {designForRender.racks.map((rack, index) => (
            <div
              key={rack.id}
              className="absolute bg-orange-400/80 w-1.5 h-1.5 rounded-full"
              style={{
                left: `${10 + (rack.position.x / 2000) * 80}%`,
                top: `${10 + (rack.position.y / 2000) * 80}%`
              }}
              title={`Rack ${rack.size}U`}
            />
          ))}
          
          {/* Viewport indicator - larger area of influence, accounts for zoom */}
          <div 
            className="absolute border-2 border-yellow-300 bg-yellow-300/16"
            style={{
              left: `${10 + (-canvasPanX / 2000) * 80}%`,
              top: `${10 + (-canvasPanY / 2000) * 80}%`,
              width: `${(1200 / canvasZoom / 2000) * 80}%`,
              height: `${(900 / canvasZoom / 2000) * 80}%`
            }}
            title={`Current View (${canvasZoom}x)`}
          />
          </div>
        </div>
      </div>
      )}

      

      

      {/* Legend */}
      {showLegend && (
        <div className={`absolute ${showCanvasToolbar ? 'top-24' : 'top-4'} left-4 bg-gray-800/90 backdrop-blur-sm p-3 rounded-lg shadow-lg text-white text-xs z-40`}>
          <div className="font-semibold mb-2">Legend</div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span>New Equipment</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded opacity-75 relative">
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-600 rounded-full"></div>
              </div>
              <span>Existing Equipment</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-0.5 bg-blue-500"></div>
              <span>New Cable</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-0.5 bg-gray-400"></div>
              <span>Existing Cable</span>
            </div>
          </div>
        </div>
      )}
      {/* Equipment Placement Indicator */}
      {draggedItem && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-auto z-50">
          <div className={`flex items-center gap-2 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-lg text-white text-sm ${isDarkTheme ? 'bg-[#3a414c]/95' : 'bg-blue-600/90'}`}>
            <span className="font-medium">Placing: {draggedItem.equipment?.name}</span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDraggedItem(null);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className={`ml-1 p-0.5 rounded transition-colors flex items-center justify-center ${isDarkTheme ? 'hover:bg-[#4a525f]' : 'hover:bg-blue-700'}`}
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Reference image placement indicator */}
      {pendingReferenceImages.length > 0 && !draggedItem && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-auto z-50">
          <div className={`flex items-center gap-2 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-lg text-white text-sm ${isDarkTheme ? 'bg-[#3a414c]/95' : 'bg-violet-600/90'}`}>
            <span className="font-medium">
              Placing reference
              {pendingReferenceImages.length > 1
                ? ` (1 of ${pendingReferenceImages.length})`
                : ''}
              {pendingReferenceImages[0]?.name
                ? `: ${pendingReferenceImages[0].name}`
                : ''}
              {' — click canvas'}
            </span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                cancelPendingReferenceImages();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className={`ml-1 p-0.5 rounded transition-colors flex items-center justify-center ${isDarkTheme ? 'hover:bg-[#4a525f]' : 'hover:bg-violet-700'}`}
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Box Drawing Mode Indicator */}
      {isDrawingBox && !draggedItem && pendingReferenceImages.length === 0 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-auto z-50">
          <div className={`flex items-center gap-2 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-lg text-white text-sm ${isDarkTheme ? 'bg-[#3a414c]/95' : 'bg-blue-600/90'}`}>
            <span className="font-medium">Drawing Box</span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDrawingBox(false);
                setSelectedBoxStyle(null);
                setBoxStart(null);
                setBoxPreview(null);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className={`ml-1 p-0.5 rounded transition-colors flex items-center justify-center ${isDarkTheme ? 'hover:bg-[#4a525f]' : 'hover:bg-blue-700'}`}
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Label Placement Mode Indicator */}
      {isLabelPlacementMode && !draggedItem && !isDrawingBox && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-auto z-50">
          <div className="flex items-center gap-2 bg-emerald-600/90 backdrop-blur-sm px-3 py-1.5 rounded-md shadow-lg text-white text-sm">
            <span className="font-medium">Placing Label</span>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsLabelPlacementMode(false);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              className="ml-1 p-0.5 hover:bg-emerald-700 rounded transition-colors flex items-center justify-center"
              title="Cancel"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Cable Run Mode Indicator */}
      {isCableRunMode && !draggedItem && (
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 pointer-events-auto z-40">
          <div className={`text-center backdrop-blur-sm p-3 rounded-lg shadow-lg text-white ${isDarkTheme ? 'bg-[#343a45]/95' : 'bg-purple-600/90'}`}>
            <h3 className="text-sm font-semibold mb-1">
              Cable Run Mode
            </h3>
            <p className={`text-xs mb-2 ${isDarkTheme ? 'text-gray-200' : 'text-purple-100'}`}>
              {selectedSourceEquipment 
                ? `Click on another device to connect to "${selectedSourceEquipment.equipment.name}"` 
                : 'Click on equipment to start a cable run'
              }
            </p>
            <button
              onClick={() => {
                setCableRunMode(false);
                setCableRoutingPopoverOpen(false);
              }}
              className={`px-3 py-1 text-xs rounded transition-colors ${isDarkTheme ? 'bg-[#454d59] hover:bg-[#525c69]' : 'bg-purple-700 hover:bg-purple-800'}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* No available ports dialog for first equipment click */}
      {noPortsDialog && (
        <div className="absolute inset-0 z-[70] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-2xl w-[360px] max-w-[92vw] border border-slate-200">
            <div className="px-4 py-3 border-b text-slate-800 font-semibold text-sm">No available ports</div>
            <div className="p-4 text-sm text-slate-700">All ports on {noPortsDialog.name} are occupied. Free a port or choose another device.</div>
            <div className="px-4 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-gray-200 hover:bg-gray-300" onClick={() => setNoPortsDialog(null)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Same device dialog */}
      {sameDeviceDialog && (
        <div className="absolute inset-0 z-[70] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-2xl w-[360px] max-w-[92vw] border border-slate-200">
            <div className="px-4 py-3 border-b text-slate-800 font-semibold text-sm">Invalid connection</div>
            <div className="p-4 text-sm text-slate-700">You can't connect a cable from {sameDeviceDialog.name} to itself.</div>
            <div className="px-4 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-gray-200 hover:bg-gray-300" onClick={() => setSameDeviceDialog(null)}>OK</button>
            </div>
          </div>
        </div>
      )}

      {/* Selected Source Equipment Highlight */}
      {isCableRunMode && selectedSourceEquipment && (
        <div 
          className="absolute border-4 border-purple-400 rounded-lg pointer-events-none animate-pulse"
          style={{
            left: selectedSourceEquipment.position.x - 2,
            top: selectedSourceEquipment.position.y - 2,
            width: 44,
            height: 44,
            zIndex: 15
          }}
        />
      )}

      {/* Shake indicator for invalid same-device selection */}
      {shakeRect && (
        <div
          className="absolute border-4 border-red-400 rounded-lg pointer-events-none animate-shake-soft"
          style={{
            left: shakeRect.left,
            top: shakeRect.top,
            width: 44,
            height: 44,
            zIndex: 16
          }}
        />
      )}

      {/* Cable type/name editing lives in CablePortEditor */}

      {/* Node Context Menu */}
      <NodeContextMenu
        isOpen={nodeContextMenu.isOpen}
        onClose={() => setNodeContextMenu({ isOpen: false, position: { x: 0, y: 0 } })}
        position={nodeContextMenu.position}
      />

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-2xl w-[380px] max-w-[92vw] border border-slate-200">
            <div className="px-4 py-3 border-b text-slate-800 font-semibold text-sm">
              {confirmDelete.type === 'equipment' ? 'Delete equipment?' : 'Delete cable run?'}
            </div>
            <div className="p-4 text-sm text-slate-700">
              Are you sure you want to delete <span className="font-medium">{confirmDelete.label}</span>{' '}
              {confirmDelete.type === 'equipment' ? 'and its connected cables' : ''}? This action cannot be undone.
            </div>
            <div className="px-4 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button className="px-3 py-1.5 text-xs rounded bg-gray-200 hover:bg-gray-300" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={() => {
                  if (confirmDelete.type === 'equipment') {
                    removeEquipment(confirmDelete.id);
                  } else {
                    deleteCableRun(confirmDelete.id);
                  }
                  setConfirmDelete(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Equipment Panel — isolated so open/close does not re-render the canvas tree */}
      <FloatingEquipmentPanelHost />
    </div>
  );
};

export default React.memo(Canvas);
